import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import SourceHighlightPanel from './SourceHighlightPanel';
import { buildSourceQuery, buildDetailsSourceQuery } from '../../../utils/sourceQuery';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Breadcrumbs,
  Link,
  Autocomplete,
  Drawer,
  DialogContentText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  WarningAmber as WarningAmberIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  TextFields as TextFieldsIcon,
  Tag as TagIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Link as LinkIcon,
  Numbers as NumbersIcon,
  List as ListIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon, // Added
  Menu, // Added
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { DataGridPro, GridToolbar, useGridApiRef } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import {
  AlertTitle,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DocumentCommentsDrawer from './DocumentCommentsDrawer';
import HistoricalActivityDrawer, { recordWorkflowHistoricalActivity } from './HistoricalActivityDrawer';
import { formatDate, formatDateTime, formatAmount, initializeFormats, getFormatConfig } from '../../../utils/global';
import { isTextIdentifierField, valuesMatchForRowFilter, isGridNumericCellValue, parseGridNumericCellValue } from '../../../utils/gridCellValueUtils';
import { useUserRights } from '../../../context/UserRightsContext';
import { hasWorkflowProductPermission } from './workflowProductScope';
import { AuditLoggers } from '../../../utils/auditLogger';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonatePlainIconButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
} from '../../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

// Utility to convert keys to lower_case_with_underscores
function toSnakeCaseKeys(obj) {
  const result = {};
  Object.keys(obj).forEach(key => {
    if (/^normalize_.*_alias$/.test(key)) {
      result[key] = obj[key];
    } else {
      const snakeKey = key
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/\s+/g, '_')
        .toLowerCase();
      result[snakeKey] = obj[key];
    }
  });
  return result;
}

const PDFViewer = ({ fileInstanceId, fastApiFileInstanceId, fileName, workflowInstanceId, workflowDefinitionSnowflakeId, onBack, alerts: parentAlerts, selectedDocumentType, onBackToWorkflows, selectedWorkflowId, productScope }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [sourceQuery, setSourceQuery] = useState(null); // NFI-5884: the value whose source is shown
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [steps, setSteps] = useState([]);
  const [stepperLoading, setStepperLoading] = useState(true);
  const [stepperError, setStepperError] = useState(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [stepDataLoading, setStepDataLoading] = useState(false);
  const [stepDataError, setStepDataError] = useState(null);
  const [investorNumber, setInvestorNumber] = useState(1);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [currentRecordIndex, setCurrentRecordIndex] = useState(0);
  const [validationAlertsByField, setValidationAlertsByField] = useState([]);
  const [selectedAlertField, setSelectedAlertField] = useState(null);
  const [highlightedRowIds, setHighlightedRowIds] = useState([]);
  const apiRef = useGridApiRef();
  const [tenantIntegrationId, setTenantIntegrationId] = useState(null);
  const [tenantIntegrationLoading, setTenantIntegrationLoading] = useState(true);
  const [viewColumns, setViewColumns] = useState([]);
  const [viewValidationErrors, setViewValidationErrors] = useState([]);
  const [fieldDropdownOptions, setFieldDropdownOptions] = useState({});
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [allFetchedSteps, setAllFetchedSteps] = useState([]);
  const [rowsList, setRowsList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dropdownFieldName, setDropdownFieldName] = useState(null);
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const stepperScrollRef = useRef(null);
  const loadStepRequestRef = useRef(0);
  const stepLoadInflightRef = useRef(null);
  const stepLoadCompletedKeyRef = useRef(null);
  const bootstrapAbortRef = useRef(null);
  const initialBootstrapDoneRef = useRef(false);
  const errorIdentifierClearCalledRef = useRef(false);
  const [stepperScrollAtStart, setStepperScrollAtStart] = useState(true);
  const [stepperScrollAtEnd, setStepperScrollAtEnd] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isGridEditMode, setIsGridEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [originalRows, setOriginalRows] = useState({});
  const [isAlreadyApproved, setIsAlreadyApproved] = useState(false);
  const [approvalStatusLoading, setApprovalStatusLoading] = useState(true);
  const [alreadyApprovedDialogOpen, setAlreadyApprovedDialogOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [validationBlockDialogOpen, setValidationBlockDialogOpen] = useState(false);
  const [fundAdministrationValidations, setFundAdministrationValidations] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [compareViewData, setCompareViewData] = useState([]);
  const [compareViewLoading, setCompareViewLoading] = useState(false);
  const [compareConfigData, setCompareConfigData] = useState(null);
  const [currentCompareRecordIndex, setCurrentCompareRecordIndex] = useState(0);
  const [mainStepDataForCompare, setMainStepDataForCompare] = useState([]);
  const [extractionData, setExtractionData] = useState(null);
  const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [materializationDialogOpen, setMaterializationDialogOpen] = useState(false);
  const [materializationStatus, setMaterializationStatus] = useState(null);
  const [materializationLoading, setMaterializationLoading] = useState(false);
  const [currentStepDetails, setCurrentStepDetails] = useState(null);
  const isHarmonateUser = isServerHarmonateUser();
  const { hasPermission, isHarmonateUser: isHarmonateFromRights } = useUserRights();
  const canEditWorkflowData = hasWorkflowProductPermission(
    hasPermission,
    Boolean(isHarmonateFromRights || isHarmonateUser),
    productScope,
    'edit'
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [amountFormat, setAmountFormat] = useState('#,###,###,##0.00');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [viewGridEnabled, setViewGridEnabled] = useState(false);
  const [printEnabled, setPrintEnabled] = useState(false);
  const [printType, setPrintType] = useState('pdf'); // 'pdf' or 'excel'
  const [approveErrorsEnabled, setApproveErrorsEnabled] = useState(false);
  const [fileInstanceDetails, setFileInstanceDetails] = useState(null);
  const [approveErrorsMap, setApproveErrorsMap] = useState(null);
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // NFI-5884: a new document, a new step or entering edit mode closes the source panel (it never shows a stale value)
  useEffect(() => { setSourceQuery(null); }, [fileInstanceId, activeStep, isGridEditMode, currentRecordIndex]);

  // Initialize formats when component mounts
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.AmountFormat) {
          setAmountFormat(config.AmountFormat);
        }
        if (config.DateFormat) {
          setDateFormat(config.DateFormat);
        }
      });
    }
  }, []);

  // New state for additional tabs
  const [activeExtractionTab, setActiveExtractionTab] = useState(0);
  const [baseInternalCasData, setBaseInternalCasData] = useState(null);
  const [baseInternalCasLoading, setBaseInternalCasLoading] = useState(false);
  const [baseInternalCasError, setBaseInternalCasError] = useState(null);
  const [landingInternalCasData, setLandingInternalCasData] = useState(null);
  const [landingInternalCasLoading, setLandingInternalCasLoading] = useState(false);
  const [landingInternalCasError, setLandingInternalCasError] = useState(null);

  const [conditionFieldsData, setConditionFieldsData] = useState(null);
  const [conditionFieldsLoading, setConditionFieldsLoading] = useState(false);
  const [skipRunConditionCheck, setSkipRunConditionCheck] = useState(false);
  const handleMaterializeTables = async (showDialog = false) => {
    try {
      if (showDialog) {
        // Dialog is already open, just update status if not already showing
        if (!materializationDialogOpen) {
          setMaterializationDialogOpen(true);
          setMaterializationStatus('Re-Calculating in progress. Please wait...');
        } else {
          // Dialog already open, just update status
          setMaterializationStatus('Re-Calculating in progress. Please wait...');
        }
        setMaterializationLoading(true);
      } else {
        setAnchorEl(null); // Close the menu
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const tenantId = localStorage.getItem('tenant_integration');
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/materialize-tables?tenant_id=${tenantId}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to materialize tables: ${response.status} ${response.statusText}`);

      if (showDialog) {
        setMaterializationStatus('Re-Calculating completed successfully!');
        setMaterializationLoading(false);
        // Close dialog after 2 seconds and reload current step
        setTimeout(() => {
          setMaterializationDialogOpen(false);
          setMaterializationStatus(null);
          // Reload the current step to show updated data
          if (activeStep >= 0 && steps.length > 0) {
            loadStepData(activeStep, steps, conditionFieldsData, { force: true });
          }
        }, 2000);
      } else {
        showSuccess('Tables materialized successfully', 'Materialize Tables');
      }
    } catch (err) {
      console.error('Error materializing tables:', err);
      if (showDialog) {
        setMaterializationStatus(`Failed to re-calculate tables: ${err.message}`);
        setMaterializationLoading(false);
        // Keep dialog open to show error, user can close manually
      } else {
        showError(`Failed to materialize tables: ${err.message}`, 'Materialize Tables Error');
      }
    }
  };
  // Fetch file instance details to get workflowDefinitionSnowflakeId
  const fetchFileInstance = async (fileInstanceId) => {
    if (!fileInstanceId) {
      console.warn('No fileInstanceId provided for fetchFileInstance');
      return null;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch file instance: ${response.status}`);
      const data = await response.json();
      console.log('File instance data:', data);
      return data;
    } catch (err) {
      console.error('Error fetching file instance:', err);
      return null;
    }
  };

  // Fetch workflow definition details to get runCondition (view_name)
  const fetchWorkflowDefinition = async (workflowDefinitionSnowflakeId) => {
    if (!workflowDefinitionSnowflakeId) {
      console.warn('No workflowDefinitionSnowflakeId provided for fetchWorkflowDefinition');
      return null;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/${workflowDefinitionSnowflakeId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch workflow definition: ${response.status}`);
      const data = await response.json();
      console.log('Workflow definition data:', data);
      return data;
    } catch (err) {
      console.error('Error fetching workflow definition:', err);
      return null;
    }
  };

  // Fetch condition fields with view_name parameter
  const fetchConditionFields = async (viewName = null, manageLoadingState = true) => {
    if (!fastApiFileInstanceId) {
      console.warn('No fastApiFileInstanceId available for fetchConditionFields');
      return null;
    }

    try {
      if (manageLoadingState) {
        setConditionFieldsLoading(true);
      }
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/internal-cas/internal-cas/process-step-condition-fields?process_id=${workflowInstanceId}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        apiUrl += `&tenant_id=${tenantId}`;
      }

      // Add view_name parameter if provided
      if (viewName) {
        apiUrl += `&view_name=${encodeURIComponent(viewName)}`;
      }

      console.log('Fetching condition fields from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch condition fields: ${response.status}`);
      const data = await response.json();

      // Assuming the API returns an array, take the first object
      const conditionData = Array.isArray(data) ? data[0] : data;
      setConditionFieldsData(conditionData);
      console.log('Condition fields data:', conditionData);
      return conditionData;
    } catch (err) {
      console.error('Error fetching condition fields:', err);
      setConditionFieldsData(null);
      return null;
    } finally {
      if (manageLoadingState) {
        setConditionFieldsLoading(false);
      }
    }
  };

  // Main function to fetch condition fields with proper chaining
  const fetchConditionFieldsWithViewName = async () => {
    try {
      setConditionFieldsLoading(true);

      // Check if fileInstanceId is available
      if (!fileInstanceId) {
        console.warn('No fileInstanceId available, fetching condition fields without view_name');
        const conditionData = await fetchConditionFields(null, false);
        return { conditionData, workflowDefinition: null };
      }

      // Step 1: Fetch file instance details
      console.log('Step 1: Fetching file instance for fileInstanceId:', fileInstanceId);
      const fileInstance = await fetchFileInstance(fileInstanceId);

      if (!fileInstance || !fileInstance.workflowDefinitionSnowflakeId) {
        console.warn('No workflowDefinitionSnowflakeId found in file instance, fetching without view_name');
        const conditionData = await fetchConditionFields(null, false);
        return { conditionData, workflowDefinition: null };
      }

      const workflowDefinitionSnowflakeId = fileInstance.workflowDefinitionSnowflakeId;
      console.log('Step 2: Found workflowDefinitionSnowflakeId:', workflowDefinitionSnowflakeId);

      // Step 2: Fetch workflow definition details
      const workflowDefinition = await fetchWorkflowDefinition(workflowDefinitionSnowflakeId);

      if (!workflowDefinition || !workflowDefinition.runCondition || workflowDefinition.runCondition.trim() === '') {
        console.warn('No runCondition found in workflow definition, fetching without view_name');
        // Set flag to skip run condition check since runCondition is empty/null
        setSkipRunConditionCheck(true);
        const conditionData = await fetchConditionFields(null, false);
        return { conditionData, workflowDefinition };
      }

      // Reset the flag if runCondition exists
      setSkipRunConditionCheck(false);
      const viewName = workflowDefinition.runCondition;
      console.log('Step 3: Found view_name (runCondition):', viewName);

      // Step 3: Fetch condition fields with view_name
      const conditionData = await fetchConditionFields(viewName, false);
      return { conditionData, workflowDefinition };
    } catch (err) {
      console.error('Error in fetchConditionFieldsWithViewName:', err);
      const conditionData = await fetchConditionFields(null, false);
      return { conditionData, workflowDefinition: null };
    } finally {
      setConditionFieldsLoading(false);
    }
  };

  // Function to evaluate run condition
  // Improved condition evaluation that handles complex conditions with || and && operators
  const evaluateRunCondition = (runCondition, conditionData) => {
    console.log('=== RUN CONDITION EVALUATION ===');
    console.log('Run Condition:', runCondition);
    console.log('Condition Data:', conditionData);

    // Handle simple cases
    if (!runCondition || runCondition.trim() === '' || runCondition.trim() === 'Always Run') {
      console.log('✅ Always run condition - returning true');
      return true;
    }

    if (!conditionData) {
      console.log('❌ No condition data available - returning false');
      return false;
    }

    try {
      // Clean and normalize the condition - remove extra whitespace and fix parentheses
      let normalizedCondition = runCondition.trim()
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/\(\s*$/, '') // Remove trailing opening parenthesis
        .replace(/^\s*\)/, ''); // Remove leading closing parenthesis

      console.log('Normalized condition:', normalizedCondition);

      // Parse and evaluate the complex condition
      const result = parseAndEvaluateCondition(normalizedCondition, conditionData);
      console.log('Final condition result:', result);
      return result;

    } catch (error) {
      console.error('Error evaluating run condition:', error);
      console.log('❌ Error in condition evaluation - returning false');
      return false;
    }
  };

  /** Real indices into `steps` that appear in the stepper for customer users when run conditions apply. */
  const getRunConditionVisibleStepIndices = (stepsArray, conditionData) => {
    const harmonateUser = isServerHarmonateUser();
    if (!stepsArray || stepsArray.length === 0) return [];
    if (harmonateUser || skipRunConditionCheck) {
      return stepsArray.map((_, i) => i);
    }
    const indices = stepsArray
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => evaluateRunCondition(step.runCondition, conditionData))
      .map(({ index }) => index);
    return indices.length === 0 ? stepsArray.map((_, i) => i) : indices;
  };

  const resolveToRunConditionVisibleStepIndex = (rawIndex, stepsArray, conditionData) => {
    if (rawIndex < 0 || !stepsArray?.length) return rawIndex;
    const visible = getRunConditionVisibleStepIndices(stepsArray, conditionData);
    if (visible.includes(rawIndex)) return rawIndex;
    return visible.find((i) => i >= rawIndex) ?? visible[0] ?? rawIndex;
  };

  const scrollStepperToStep = (stepIndex, sortedSteps, conditionData) => {
    if (stepIndex < 0 || !sortedSteps?.length) return;
    setTimeout(() => {
      if (!stepperScrollRef.current) return;
      const stepper = stepperScrollRef.current;
      const stepLabels = stepper.querySelectorAll('.MuiStepLabel-root');
      const vis = getRunConditionVisibleStepIndices(sortedSteps, conditionData);
      const labelIdx = vis.indexOf(stepIndex);
      if (labelIdx >= 0 && stepLabels[labelIdx]) {
        stepLabels[labelIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, 0);
  };

  // Tokenize the condition string into components
  const tokenizeCondition = (condition) => {
    const tokens = [];
    let current = '';
    let i = 0;

    while (i < condition.length) {
      const char = condition[i];
      const nextChar = condition[i + 1];

      if (char === '(' || char === ')') {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push(char);
        i++;
      } else if (char === '&' && nextChar === '&') {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push('&&');
        i += 2;
      } else if (char === '|' && nextChar === '|') {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push('||');
        i += 2;
      } else if (char === '!' && nextChar === '=') {
        current += '!=';
        i += 2;
      } else if (char === '=' && condition[i - 1] !== '!' && condition[i - 1] !== '=') {
        current += '=';
        i++;
      } else {
        current += char;
        i++;
      }
    }

    if (current.trim()) {
      tokens.push(current.trim());
    }

    return tokens.filter(token => token !== '');
  };

  // Parse and evaluate complex conditions with proper precedence
  const parseAndEvaluateCondition = (condition, conditionData) => {
    const tokens = tokenizeCondition(condition);
    console.log('Tokenized condition:', tokens);

    // Convert infix to postfix notation (Shunting Yard algorithm)
    const postfix = infixToPostfix(tokens);
    console.log('Postfix notation:', postfix);

    // Evaluate postfix expression
    return evaluatePostfix(postfix, conditionData);
  };

  // Convert infix notation to postfix (handles operator precedence)
  const infixToPostfix = (tokens) => {
    const output = [];
    const operators = [];
    const precedence = { '||': 1, '&&': 2 };
    const isOperator = (token) => token === '&&' || token === '||';

    for (const token of tokens) {
      if (token === '(') {
        operators.push(token);
      } else if (token === ')') {
        while (operators.length > 0 && operators[operators.length - 1] !== '(') {
          output.push(operators.pop());
        }
        operators.pop(); // Remove the '('
      } else if (isOperator(token)) {
        while (
          operators.length > 0 &&
          operators[operators.length - 1] !== '(' &&
          precedence[operators[operators.length - 1]] >= precedence[token]
        ) {
          output.push(operators.pop());
        }
        operators.push(token);
      } else {
        // It's a condition expression
        output.push(token);
      }
    }

    while (operators.length > 0) {
      output.push(operators.pop());
    }

    return output;
  };

  // Evaluate postfix expression
  const evaluatePostfix = (postfix, conditionData) => {
    const stack = [];

    for (const token of postfix) {
      if (token === '&&') {
        const b = stack.pop();
        const a = stack.pop();
        stack.push(a && b);
      } else if (token === '||') {
        const b = stack.pop();
        const a = stack.pop();
        stack.push(a || b);
      } else {
        // It's a condition expression - evaluate it
        const result = evaluateSingleCondition(token, conditionData);
        console.log(`Evaluating: "${token}" = ${result}`);
        stack.push(result);
      }
    }

    return stack.length > 0 ? stack[0] : false;
  };

  const evaluateSingleCondition = (condition, conditionData) => {
    // Remove extra parentheses and whitespace
    const cleanCondition = condition.replace(/[()]/g, '').trim();
    console.log('Evaluating single condition:', cleanCondition);

    // Handle different operators
    let operator = '=';
    let parts = [];

    if (cleanCondition.includes('!=')) {
      operator = '!=';
      parts = cleanCondition.split('!=').map(p => p.trim());
    } else if (cleanCondition.includes('=')) {
      operator = '=';
      parts = cleanCondition.split('=').map(p => p.trim());
    } else {
      console.log('❌ No recognized operator found');
      return false;
    }

    if (parts.length !== 2) {
      console.log('❌ Invalid condition format - expected 2 parts, got:', parts.length);
      return false;
    }

    const [fieldPart, expectedValue] = parts;

    // Extract field name from Normalize..FIELD format
    const normalizePattern = /^Normalize\.\.(.+)$/;
    const match = fieldPart.match(normalizePattern);

    if (!match) {
      console.log('❌ Invalid field format - expected Normalize..FIELD, got:', fieldPart);
      return false;
    }

    const fieldName = match[1];
    console.log('Extracted field name:', fieldName);
    console.log('Expected value:', expectedValue);
    console.log('Operator:', operator);

    // Enhanced field name variations for better matching
    const fieldVariations = [
      fieldName, // Original: ENDING_NAV_INVESTOR_QTD
      fieldName.toLowerCase(), // ending_nav_investor_qtd
      fieldName.toLowerCase().replace(/\s+/g, '_'), // ending_nav_investor_qtd
      fieldName.toUpperCase(), // ENDING_NAV_INVESTOR_QTD
      fieldName.toUpperCase().replace(/\s+/g, '_'), // ENDING_NAV_INVESTOR_QTD
      // Add camelCase variation
      fieldName.toLowerCase().replace(/_([a-z])/g, (match, letter) => letter.toUpperCase()),
      // Add variations without underscores
      fieldName.replace(/_/g, '').toLowerCase(),
      fieldName.replace(/_/g, '').toUpperCase(),
    ];

    let actualValue = undefined;
    let usedFieldName = null;

    // Try each variation until we find a match
    for (const variation of fieldVariations) {
      if (conditionData.hasOwnProperty(variation)) {
        actualValue = conditionData[variation];
        usedFieldName = variation;
        break;
      }
    }

    console.log('Field variations tried:', fieldVariations);
    console.log('Used field name:', usedFieldName);
    console.log('Actual value:', actualValue);

    if (actualValue === undefined) {
      console.log('❌ Field not found in condition data');
      console.log('Available fields in condition data:', Object.keys(conditionData));
      return false;
    }

    // Perform comparison based on operator
    let result = false;

    if (operator === '=') {
      result = compareValues(actualValue, expectedValue, '=');
    } else if (operator === '!=') {
      result = compareValues(actualValue, expectedValue, '!=');
    }

    console.log(`Comparison: ${actualValue} ${operator} ${expectedValue} = ${result}`);
    return result;
  };

  const compareValues = (actual, expected, operator) => {
    // Handle null/undefined/empty string as 0 for numeric comparisons
    const normalizeNumericValue = (val) => {
      if (val === null || val === undefined || val === '' || val === 'null' || val === 'undefined') {
        return 0;
      }
      const num = parseFloat(val);
      return isNaN(num) ? val : num;
    };

    const actualNormalized = normalizeNumericValue(actual);
    const expectedNormalized = normalizeNumericValue(expected);

    console.log('Normalized values for comparison:', {
      actual: actualNormalized,
      expected: expectedNormalized,
      originalActual: actual,
      originalExpected: expected
    });

    // Handle special case for YTD_QTD_ITD field
    if (expected === 'YTD_ITD') {
      // This is a special comparison - check if the field value equals the literal string 'YTD_ITD'
      const actualStr = String(actual);
      if (operator === '=') {
        return actualStr === 'YTD_ITD';
      } else if (operator === '!=') {
        return actualStr !== 'YTD_ITD';
      }
    }

    // Handle boolean comparison
    if (expected.toLowerCase() === 'true') {
      const actualBool = actual === true || actual === 'true' || actual === 1 || actual === '1';
      return operator === '=' ? actualBool : !actualBool;
    }

    if (expected.toLowerCase() === 'false') {
      const actualBool = actual === false || actual === 'false' || actual === 0 || actual === '0';
      return operator === '=' ? actualBool : !actualBool;
    }

    // Handle numeric comparison
    if (typeof actualNormalized === 'number' && typeof expectedNormalized === 'number') {
      if (operator === '=') {
        return actualNormalized === expectedNormalized;
      } else if (operator === '!=') {
        return actualNormalized !== expectedNormalized;
      }
    }

    // Handle string comparison as fallback
    const actualStr = String(actualNormalized);
    const expectedStr = String(expectedNormalized);

    if (operator === '=') {
      return actualStr === expectedStr;
    } else if (operator === '!=') {
      return actualStr !== expectedStr;
    }

    return false;
  };

  const SUMMARY_APPROVAL_STEP_NAMES = ['Summary', 'Investor Summary', 'Combined Summary', 'Combined Summery'];

  const isSummaryApprovalStep = () => {
    const stepName = steps[activeStep]?.stepName;
    return SUMMARY_APPROVAL_STEP_NAMES.includes(stepName);
  };

  const isCurrentStepTransform = () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return false;
    if (currentStep.stepType !== 'TRANSFORM') return false;
    const excludedSteps = ['GP', 'Fund', 'Investors'];
    return !excludedSteps.includes(currentStep.stepName);
  };

  const isCurrentStepCompare = () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return false;
    return currentStep.stepType === 'COMPARE';
  };

  const isCurrentStepForm = () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return false;
    return currentStep.stepType === 'FORM';
  };

  const isCurrentStepEditable = () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return false;
    const alwaysEditableSteps = ['GP', 'Fund', 'Investors'];
    // Check if it's one of the always editable steps
    if (alwaysEditableSteps.includes(currentStep.stepName)) {
      return true;
    }
    // Check if viewColumns have any editable fields (for dynamic editable steps)
    if (viewColumns && viewColumns.length > 0) {
      const hasEditableFields = viewColumns.some(col => col.editable === true);
      if (hasEditableFields) {
        return true;
      }
    }
    // Legacy check: non-TRANSFORM steps are editable
    return currentStep.stepType !== 'TRANSFORM';
  };

  // Filter comparison data based on selected row value
  const filteredComparisonData = useMemo(() => {
    if (!selectedRow || !dropdownFieldName || !comparisonData.length) {
      return comparisonData;
    }

    // For COMPARE steps, the data structure is different (each row is a field)
    // We need to check if this is a COMPARE step structure
    const isCompareStructure = comparisonData[0] && comparisonData[0].hasOwnProperty('field');

    if (isCompareStructure) {
      // For COMPARE steps, filter mainStepDataForCompare and regenerate grid
      // But for now, return all data as filtering COMPARE grid requires regenerating it
      // This will be handled separately if needed
      console.log('COMPARE step structure detected - filtering not yet implemented for COMPARE steps');
      return comparisonData;
    }

    // For regular steps, filter by the dropdown field
    const firstRow = comparisonData[0];
    let actualFieldName = null;
    const variations = [
      dropdownFieldName,
      dropdownFieldName.toUpperCase(),
      dropdownFieldName.toLowerCase(),
      dropdownFieldName.replace(/\s+/g, '_'),
      dropdownFieldName.replace(/\s+/g, '_').toUpperCase(),
      dropdownFieldName.replace(/\s+/g, '_').toLowerCase(),
    ];

    // Find which variation exists in the data
    for (const variation of variations) {
      if (firstRow.hasOwnProperty(variation)) {
        actualFieldName = variation;
        break;
      }
    }

    if (!actualFieldName) {
      console.warn('Dropdown field not found in comparison data:', dropdownFieldName, 'Available fields:', Object.keys(firstRow));
      return comparisonData;
    }

    const filtered = comparisonData.filter(row => {
      const rowValue = row[actualFieldName];
      return valuesMatchForRowFilter(rowValue, selectedRow);
    });

    console.log('Filtering data:', {
      selectedRow,
      dropdownFieldName,
      actualFieldName,
      totalRows: comparisonData.length,
      filteredRows: filtered.length,
      sampleRow: filtered[0]
    });

    return filtered;
  }, [comparisonData, selectedRow, dropdownFieldName]);

  const runConditionVisibleStepIndices = useMemo(
    () => getRunConditionVisibleStepIndices(steps, conditionFieldsData),
    [steps, conditionFieldsData, skipRunConditionCheck] // eslint-disable-line react-hooks/exhaustive-deps -- helper uses evaluateRunCondition from closure
  );

  const stepperUiActiveStep = useMemo(() => {
    if (activeStep < 0 || runConditionVisibleStepIndices.length === 0) return Math.max(0, activeStep);
    const idx = runConditionVisibleStepIndices.indexOf(activeStep);
    return idx === -1 ? 0 : idx;
  }, [activeStep, runConditionVisibleStepIndices]);

  // Reset currentRecordIndex when filtered data changes
  useEffect(() => {
    if (filteredComparisonData.length > 0 && currentRecordIndex >= filteredComparisonData.length) {
      setCurrentRecordIndex(0);
    }
  }, [filteredComparisonData, currentRecordIndex]);

  // Navigate to matching record when dropdown value is selected
  useEffect(() => {
    if (!selectedRow || !dropdownFieldName) return;

    if (isCurrentStepCompare() && mainStepDataForCompare.length > 0) {
      // For COMPARE steps, find the record index in mainStepDataForCompare
      const firstRecord = mainStepDataForCompare[0];
      let actualFieldName = null;
      const variations = [
        dropdownFieldName,
        dropdownFieldName.toUpperCase(),
        dropdownFieldName.toLowerCase(),
        dropdownFieldName.replace(/\s+/g, '_'),
        dropdownFieldName.replace(/\s+/g, '_').toUpperCase(),
        dropdownFieldName.replace(/\s+/g, '_').toLowerCase(),
      ];

      // Find which variation exists in the data
      for (const variation of variations) {
        if (firstRecord.hasOwnProperty(variation)) {
          actualFieldName = variation;
          break;
        }
      }

      if (actualFieldName) {
        const matchingIndex = mainStepDataForCompare.findIndex(record => {
          const recordValue = record[actualFieldName];
          return valuesMatchForRowFilter(recordValue, selectedRow);
        });

        if (matchingIndex >= 0) {
          console.log(`Navigating to COMPARE record index ${matchingIndex} for value: ${selectedRow}`, {
            actualFieldName,
            totalRecords: mainStepDataForCompare.length,
            currentIndex: currentCompareRecordIndex
          });
          setCurrentCompareRecordIndex(matchingIndex);
        } else {
          console.warn('No matching record found for value:', selectedRow, 'in field:', actualFieldName);
          console.log('Sample record values:', mainStepDataForCompare.slice(0, 3).map(r => ({ [actualFieldName]: r[actualFieldName] })));
        }
      } else {
        console.warn('Dropdown field not found in mainStepDataForCompare:', dropdownFieldName, 'Available fields:', Object.keys(firstRecord));
      }
    } else if (!isCurrentStepCompare() && comparisonData.length > 0) {
      // For regular steps, navigate to the first matching record in filtered data
      // The filtering is already handled by filteredComparisonData, just reset to first record
      if (filteredComparisonData.length > 0) {
        setCurrentRecordIndex(0);
        console.log(`Filtered to ${filteredComparisonData.length} records for value: ${selectedRow}`);
      }
    }
  }, [selectedRow, dropdownFieldName, mainStepDataForCompare, comparisonData, filteredComparisonData, isCurrentStepCompare, currentCompareRecordIndex]);

  const getCurrentRecordData = () => {
    if (!isCurrentStepTransform() || !filteredComparisonData.length) return null;
    return filteredComparisonData[currentRecordIndex] || filteredComparisonData[0];
  };

  const goToNextRecord = () => {
    if (currentRecordIndex < filteredComparisonData.length - 1) {
      setCurrentRecordIndex(currentRecordIndex + 1);
    }
  };

  const goToPreviousRecord = () => {
    if (currentRecordIndex > 0) {
      setCurrentRecordIndex(currentRecordIndex - 1);
    }
  };

  const goToNextCompareRecord = () => {
    if (isCurrentStepCompare() && mainStepDataForCompare.length > 1 && currentCompareRecordIndex < mainStepDataForCompare.length - 1) {
      setCurrentCompareRecordIndex(currentCompareRecordIndex + 1);
    }
  };

  const goToPreviousCompareRecord = () => {
    if (isCurrentStepCompare() && mainStepDataForCompare.length > 1 && currentCompareRecordIndex > 0) {
      setCurrentCompareRecordIndex(currentCompareRecordIndex - 1);
    }
  };

  const updateCompareGridData = () => {
    if (isCurrentStepCompare() && compareConfigData && mainStepDataForCompare.length > 0) {
      const updatedCompareGridData = createCompareGridData(
        compareConfigData,
        mainStepDataForCompare,
        compareViewData,
        currentCompareRecordIndex
      );
      setComparisonData(updatedCompareGridData);
    }
  };

  useEffect(() => {
    setCurrentRecordIndex(0);
    setCurrentCompareRecordIndex(0);
    // Prevent showing previous step's alert count/icons during step transition
    setValidationAlertsByField([]);
    setSelectedAlertField(null);
    setHighlightedRowIds([]);
  }, [activeStep]);

  useEffect(() => {
    updateCompareGridData();
    // Clear any existing highlighting when record changes
    setSelectedAlertField(null);
  }, [currentCompareRecordIndex, compareViewData, compareConfigData, mainStepDataForCompare]);


  const updateArrowVisibility = () => {
    if (!stepperScrollRef.current) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    const { scrollWidth, clientWidth, scrollLeft } = stepperScrollRef.current;
    setShowLeftArrow(scrollLeft > 5);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 5);
  };

  const handleStepperScroll = () => {
    if (!stepperScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = stepperScrollRef.current;
    setStepperScrollAtStart(scrollLeft <= 5);
    setStepperScrollAtEnd(scrollLeft + clientWidth >= scrollWidth - 5);
    updateArrowVisibility();
  };

  useEffect(() => {
    updateArrowVisibility();
  }, [steps, stepperLoading]);

  useEffect(() => {
    if (!stepperScrollRef.current) return;
    const stepper = stepperScrollRef.current;
    const stepLabels = stepper.querySelectorAll('.MuiStepLabel-root');
    const labelIdx = runConditionVisibleStepIndices.indexOf(activeStep);
    if (labelIdx >= 0 && stepLabels[labelIdx]) {
      stepLabels[labelIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeStep, runConditionVisibleStepIndices]);

  // Additional useEffect to ensure stepper scrolls to active step after steps are fully rendered
  useEffect(() => {
    if (activeStep >= 0 && steps.length > 0 && !stepperLoading) {
      // Use requestAnimationFrame to ensure stepper is fully rendered after the next paint
      const frameId = requestAnimationFrame(() => {
        if (stepperScrollRef.current) {
          const stepper = stepperScrollRef.current;
          const stepLabels = stepper.querySelectorAll('.MuiStepLabel-root');
          const labelIdx = runConditionVisibleStepIndices.indexOf(activeStep);
          if (labelIdx >= 0 && stepLabels[labelIdx]) {
            stepLabels[labelIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        }
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [activeStep, steps, stepperLoading, runConditionVisibleStepIndices]);

  // Use useLayoutEffect for more reliable scrolling after DOM updates
  useLayoutEffect(() => {
    if (activeStep >= 0 && steps.length > 0 && !stepperLoading) {
      if (stepperScrollRef.current) {
        const stepper = stepperScrollRef.current;
        const stepLabels = stepper.querySelectorAll('.MuiStepLabel-root');
        const labelIdx = runConditionVisibleStepIndices.indexOf(activeStep);
        if (labelIdx >= 0 && stepLabels[labelIdx]) {
          stepLabels[labelIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    }
  }, [activeStep, steps, stepperLoading, runConditionVisibleStepIndices]);

  const handleStepLabelClick = (index, e) => {
    // Prevent step navigation when in edit mode
    if (isGridEditMode) {
      showInfo('Please save or cancel your changes before navigating to another step', 'Edit Mode Active');
      return;
    }
    const blockingStepIndex = getBlockingValidationStepIndexFromPayloads(steps, fundAdministrationValidations, approveErrorsMap);
    if (blockingStepIndex !== -1 && index > blockingStepIndex) {
      const blockingStepName = steps?.[blockingStepIndex]?.stepName || `Step ${blockingStepIndex + 1}`;
      showInfo(`Resolve or approve validation errors in "${blockingStepName}" before moving to next steps`, 'Step blocked');
      return;
    }
    setActiveStep(index);
    handleStepClick(index);
    if (stepperScrollRef.current) {
      const stepper = stepperScrollRef.current;
      const stepLabels = stepper.querySelectorAll('.MuiStepLabel-root');
      const labelIdx = runConditionVisibleStepIndices.indexOf(index);
      if (labelIdx >= 0 && stepLabels[labelIdx]) {
        stepLabels[labelIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  };

  const fetchApprovalStatus = async () => {
    try {
      setApprovalStatusLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      console.log(`Fetching approval status for file instance ID: ${fileInstanceId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/approval-status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error(`Failed to fetch approval status: ${response.status} ${response.statusText}`);
      const data = await response.json();
      console.log('Approval Status API Response:', data);
      setIsAlreadyApproved(data.approvedStatus === true);
      if (data.approvedStatus === true) showInfo('Document is already approved');
    } catch (err) {
      console.error('Error fetching approval status:', err);
      showError(`Failed to fetch approval status: ${err.message}`, 'Approval Status Error');
      setIsAlreadyApproved(false);
    } finally {
      setApprovalStatusLoading(false);
    }
  };

  const fetchPdfUrl = async (fileInstanceId) => {
    try {
      setPdfLoading(true);
      setPdfError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      console.log(`Fetching PDF URL for file instance ID: ${fileInstanceId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/presigned-url`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error(`Failed to fetch PDF URL: ${response.status} ${response.statusText}`);
      const data = await response.json();
      console.log('PDF API Response:', data);
      if (data.url) {
        setPdfUrl(data.url);
        showSuccess('PDF loaded successfully');
      } else {
        throw new Error('No URL returned from API');
      }
    } catch (err) {
      console.error('Error fetching PDF URL:', err);
      setPdfError(err.message);
      showError(`Failed to load PDF: ${err.message}`, 'PDF Loading Error');
    } finally {
      setPdfLoading(false);
    }
  };

  const generateDynamicColumns = (data) => {
    if (!data || data.length === 0) return [];
    const firstObject = data[0];
    const keys = Object.keys(firstObject);

    const getColumnConfig = (key, value) => {
      const lowerKey = key.toLowerCase();
      const baseConfig = {
        field: key,
        headerName: formatHeaderName(key),
        width: 150,
        filterable: true,
        sortable: true,
      };

      if (lowerKey.endsWith('_validation') || key.endsWith('_VALIDATION')) {
        return {
          ...baseConfig,
          width: 120,
          type: 'boolean',
          valueGetter: (params) => {
            const val = params.row[key];
            return val === true || val === 'true' || val === 1 || val === '1';
          },
          renderCell: (params) => {
            const val = params.row[key];
            const isValid = val === true || val === 'true' || val === 1 || val === '1';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!isValid && (
                  <WarningAmberIcon sx={{ color: '#f44336', fontSize: '1.1em' }} titleAccess="Validation failed" />
                )}
                <Typography sx={{
                  fontSize: '0.7rem',
                  color: isValid ? '#4caf50' : '#f44336',
                  fontWeight: 500
                }}>
                  {isValid ? 'Valid' : 'Invalid'}
                </Typography>
              </Box>
            );
          },
        };
      }

      if (lowerKey.includes('date') || lowerKey.includes('created') || lowerKey.includes('received')) {
        return {
          ...baseConfig,
          width: 180,
          type: 'date',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Date field ${key} for row ${params.row.id}:`, val);
            if (!val) return null;
            
            // For static dates from documents (YYYY-MM-DD), avoid timezone conversion
            let date;
            if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
              const [y, m, d] = val.split('-').map(Number);
              date = new Date(y, m - 1, d);
            } else {
              date = new Date(val);
            }
            
            return isNaN(date.getTime()) ? val : date;
          },
          renderCell: (params) => {
            const dateValue = params.value;
            return (
              <Typography sx={{ fontSize: '0.7rem' }}>
                {dateValue ? formatDate(dateValue) : ''}
              </Typography>
            );
          },
        };
      }

      if (lowerKey.includes('amount') || lowerKey.includes('balance') ||
        lowerKey.includes('capital') || lowerKey.includes('nav') ||
        lowerKey.includes('fee') || lowerKey.includes('income') ||
        lowerKey.includes('expense') || lowerKey.includes('gain') ||
        lowerKey.includes('loss') || lowerKey.includes('contribution') ||
        lowerKey.includes('distribution') || lowerKey.includes('commitment') ||
        lowerKey.includes('proceeds') || lowerKey.includes('costs') ||
        lowerKey.includes('allocation') || lowerKey.includes('ytd') ||
        lowerKey.includes('itd')) {
        return {
          ...baseConfig,
          width: 160,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Financial field ${key} for row ${params.row.id}:`, val);
            if (val === null || val === undefined || val === '') return 0;
            const numVal = parseFloat(val);
            return isNaN(numVal) ? 0 : numVal;
          },
          renderCell: (params) => {
            const numValue = params.value || 0;
            const isNegative = numValue < 0;
            return (
              <Typography sx={{
                fontSize: '0.7rem',
                color: isNegative ? '#f44336' : 'inherit',
                textAlign: 'right',
                width: '100%'
              }}>
                {numValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'currency',
                  currency: 'USD'
                })}
              </Typography>
            );
          },
        };
      }

      if (lowerKey.includes('delta') || lowerKey.includes('change') || lowerKey.includes('compare')) {
        return {
          ...baseConfig,
          width: 120,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Delta field ${key} for row ${params.row.id}:`, val);
            if (val === null || val === undefined || val === '') return 0;
            const numVal = parseFloat(val);
            return isNaN(numVal) ? 0 : numVal;
          },
          renderCell: (params) => {
            const numValue = params.value || 0;
            const isZero = numValue === 0;
            const isPositive = numValue > 0;
            return (
              <Typography sx={{
                fontSize: '0.7rem',
                color: isZero ? 'text.primary' : (isPositive ? '#4caf50' : '#f44336'),
                fontWeight: !isZero ? 600 : 400,
                textAlign: 'center'
              }}>
                {isZero ? '0.00' : (isPositive ? '+' : '') + numValue.toFixed(2)}
              </Typography>
            );
          },
        };
      }

      if (lowerKey.includes('percent') || lowerKey.includes('rate') || lowerKey.includes('tolerance')) {
        return {
          ...baseConfig,
          width: 110,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Percentage field ${key} for row ${params.row.id}:`, val);
            if (val === null || val === undefined || val === '') return 0;
            const numVal = parseFloat(val);
            return isNaN(numVal) ? 0 : numVal;
          },
          renderCell: (params) => {
            const numValue = params.value || 0;
            return (
              <Typography sx={{ fontSize: '0.7rem', textAlign: 'right' }}>
                {numValue.toFixed(6)}%
              </Typography>
            );
          },
        };
      }

      if (lowerKey.includes('quarter') && !lowerKey.includes('label')) {
        return {
          ...baseConfig,
          width: 100,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Quarter field ${key} for row ${params.row.id}:`, val);
            return parseInt(val) || 0;
          },
          renderCell: (params) => (
            <Typography sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (lowerKey.includes('year') || lowerKey.includes('month')) {
        return {
          ...baseConfig,
          width: 80,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Year/Month field ${key} for row ${params.row.id}:`, val);
            return parseInt(val) || 0;
          },
          renderCell: (params) => (
            <Typography sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (lowerKey.includes('id') || lowerKey.includes('code')) {
        return {
          ...baseConfig,
          width: 100,
          align: 'center',
          headerAlign: 'center',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`ID field ${key} for row ${params.row.id}:`, val);
            return val || '';
          },
          renderCell: (params) => (
            <Typography sx={{ fontSize: '0.7rem', textAlign: 'center', fontFamily: 'monospace' }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (lowerKey.includes('name') || lowerKey.includes('description') ||
        lowerKey.includes('investor') || lowerKey.includes('fund') ||
        lowerKey.includes('statement') || lowerKey.includes('type')) {
        return {
          ...baseConfig,
          width: lowerKey.includes('description') ? 250 : 200,
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Name field ${key} for row ${params.row.id}:`, val);
            return val || '';
          },
          renderCell: (params) => (
            <Typography sx={{
              fontSize: '0.7rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (lowerKey.includes('label')) {
        return {
          ...baseConfig,
          width: 120,
          align: 'center',
          headerAlign: 'center',
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`Label field ${key} for row ${params.row.id}:`, val);
            return val || '';
          },
          renderCell: (params) => (
            <Typography sx={{
              fontSize: '0.7rem',
              textAlign: 'center',
              fontWeight: 500,
              color: '#1976d2'
            }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (typeof value === 'string') {
        return {
          ...baseConfig,
          width: 150,
          valueGetter: (params) => {
            const val = params.row[key];
            console.log(`String field ${key} for row ${params.row.id}:`, val);
            return val || '';
          },
          renderCell: (params) => (
            <Typography sx={{ fontSize: '0.7rem' }}>
              {params.value}
            </Typography>
          ),
        };
      }

      return {
        ...baseConfig,
        valueGetter: (params) => {
          const val = params.row[key];
          console.log(`Default field ${key} for row ${params.row.id}:`, val);
          if (typeof val === 'number') return val;
          if (typeof val === 'boolean') return val;
          return val || '';
        },
        renderCell: (params) => (
          <Typography sx={{ fontSize: '0.7rem' }}>
            {String(params.value)}
          </Typography>
        ),
      };
    };

    const columns = keys.map(key => getColumnConfig(key, firstObject[key]));
    return columns;
  };

  // Format a date value using formatDate
  const formatDateValue = (value) => {
    if (!value) return value;
    try {
      let dateValue = value;
      // For static dates from documents (YYYY-MM-DD), avoid timezone conversion
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        dateValue = new Date(y, m - 1, d);
      }
      return formatDate(dateValue);
    } catch (e) {
      console.warn('Failed to format date:', value, e);
      return value;
    }
  };

  // Prepare data for grid by ensuring each item has an ID and formatting fields
  const prepareDataForGrid = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    return rawData.map((item, index) => {
      const cleanedItem = {};
      cleanedItem.id = item.id || item.ID || `${item.PROCESS_ID}_${item.INVESTOR}_${index}` || `row_${index + 1}`;
      Object.keys(item).forEach(key => {
        cleanedItem[key] = item[key];
      });
      return cleanedItem;
    });
  };

  // Process data using DataType from column metadata for proper field formatting
  const processData = (data, columnMetadata = []) => {
    if (!data || !Array.isArray(data)) return [];

    console.log('Processing data with column metadata:', columnMetadata);

    // Create a map of field names to their data types for faster lookup
    const fieldTypes = {};
    columnMetadata.forEach(col => {
      if (col.name && col.DataType) {
        fieldTypes[col.name] = col.DataType;
        console.log(`Field: ${col.name}, Type: ${col.DataType}`);
      }
    });

    console.log('Field types map:', fieldTypes);

    // Get the amount format from config or use default
    const formatCfg = getFormatConfig();
    const amountFormat = formatCfg?.AmountFormat || '#,###,###,##0.00';
    console.log('Using amount format:', amountFormat);

    return data.map(item => {
      const processedItem = { ...item };
      Object.keys(processedItem).forEach(key => {
        const value = processedItem[key];
        if (value === null || value === undefined || value === '') return;

        const dataType = fieldTypes[key] || '';
        const lowerDataType = dataType.toLowerCase();
        const lowerKey = key.toLowerCase();

        // Skip processing for fund / investor name fields (leading numbers are not amounts)
        if (key === 'PROVIDED_FUND_NAME' || key === 'REFERENCE_FUND_NAME' ||
          lowerKey.includes('fund_name') || lowerKey.includes('fundname') ||
          isTextIdentifierField(key)) {
          return;
        }

        // Format based on data type from API
        if (lowerDataType.includes('date') || lowerDataType.includes('time')) {
          const formatCfg = getFormatConfig();
          const currentDateFormat = formatCfg?.DateFormat || dateFormat;
          
          let dateValue = value;
          // For static dates from documents (YYYY-MM-DD), avoid timezone conversion
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [y, m, d] = value.split('-').map(Number);
            dateValue = new Date(y, m - 1, d);
          }
          
          processedItem[key] = formatDate(dateValue, currentDateFormat);
        }
        // Format numeric fields based on data type
        else if (!isTextIdentifierField(key) &&
          (lowerDataType.includes('int') ||
          lowerDataType.includes('num') ||
          lowerDataType.includes('decimal') ||
          lowerDataType.includes('float') ||
          lowerDataType.includes('double'))) {
          try {
            const num = isGridNumericCellValue(value)
              ? parseGridNumericCellValue(value)
              : typeof value === 'number'
              ? value
              : NaN;
            if (!isNaN(num)) {
              processedItem[key] = formatAmount(num, amountFormat);
              console.log(`Formatted number field ${key}:`, value, '->', processedItem[key]);
            }
          } catch (e) {
            console.warn(`Error formatting number field ${key}:`, e);
          }
        }
      });
      return processedItem;
    });
  };

  const formatHeaderName = (key) => {
    if (!key) return '';
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Function to fetch and process compare view data
  const fetchCompareViewData = async (compareViewName) => {
    setCompareViewLoading(true);
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');

    const queryParams = new URLSearchParams();
    if (compareViewName) queryParams.append('view_name', compareViewName);
    const tenantId = localStorage.getItem('tenant_integration');
    if (tenantId) queryParams.set('tenant_id', tenantId);
    if (fastApiFileInstanceId) queryParams.set('file_instance_id', fastApiFileInstanceId);
    if (workflowInstanceId) queryParams.set('workflow_instance_id', workflowInstanceId);

    try {
      // First fetch the column metadata
      const metadataResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${compareViewName}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      let columnMetadata = [];
      if (metadataResponse.ok) {
        const metadata = await metadataResponse.json();
        columnMetadata = metadata.data?.columns || [];
        console.log('Fetched column metadata:', columnMetadata);
      } else {
        console.warn('Failed to fetch column metadata, using empty array');
      }

      // Then fetch the actual data
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/step?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error(`Failed to fetch compare view data: ${response.status}`);
      const data = await response.json();

      let preparedData = [];
      if (data && data.data) {
        // Pass the column metadata for proper formatting
        preparedData = processData(prepareDataForGrid(data.data), columnMetadata);
        setCompareViewData(preparedData);
      } else {
        setCompareViewData([]);
      }
      return preparedData;
    } catch (error) {
      console.error('Error fetching compare view data:', error);
      setCompareViewData([]);
      return [];
    } finally {
      setCompareViewLoading(false);
    }
  };

  const createCompareGridData = (compareConfig, mainStepData, compareViewData, recordIndex = 0) => {
    if (!compareConfig || !compareConfig.compare || !mainStepData || mainStepData.length === 0) {
      return [];
    }

    const { field: fieldArray, source_columns = [], compare_columns = [], result_columns = [] } = compareConfig.compare;

    // Combine all column metadata from source, compare, and result columns
    const allColumnMetadata = [...(source_columns || []), ...(compare_columns || []), ...(result_columns || [])];
    console.log('All column metadata for compare view:', allColumnMetadata);

    // Create a map for quick lookups
    const sourceColMap = {};
    const compareColMap = {};
    const resultColMap = {};

    source_columns?.forEach(col => {
      sourceColMap[col.fieldName] = col.ColName;
    });

    compare_columns?.forEach(col => {
      compareColMap[col.fieldName] = col.ColName;
    });

    result_columns?.forEach(col => {
      resultColMap[col.fieldName] = col.ColName;
    });

    // Get the record at specified index from main step data for source and result values
    const mainRecord = mainStepData[recordIndex] || mainStepData[0] || {};

    // Find matching compare record based on SURROGATE_KEY field constraint
    let compareRecord = {};
    if (compareViewData && compareViewData.length > 0) {
      // Get the SURROGATE_KEY field column name from source mapping
      // Fallback to 'SURROGATE_KEY' literal if not found in mapping
      const primaryKeySourceColName = sourceColMap['SURROGATE_KEY'] || 'SURROGATE_KEY';
      const primaryKeyCompareColName = compareColMap['SURROGATE_KEY'] || 'SURROGATE_KEY';

      console.log('Compare matching debug:', {
        primaryKeySourceColName,
        primaryKeyCompareColName,
        mainRecordValue: mainRecord[primaryKeySourceColName],
        compareViewDataSize: compareViewData.length
      });

      // Get the primary key value from the main record
      const sourcePKValue = mainRecord[primaryKeySourceColName];

      if (sourcePKValue !== undefined && sourcePKValue !== null) {
        // Find the compare record with matching primary key (case-insensitive and trimmed)
        const normalizedSourceValue = String(sourcePKValue).trim().toLowerCase();
        
        const matchingCompareRecord = compareViewData.find(record => {
          const compareValue = record[primaryKeyCompareColName];
          if (compareValue === undefined || compareValue === null) return false;
          return String(compareValue).trim().toLowerCase() === normalizedSourceValue;
        });

        if (matchingCompareRecord) {
          compareRecord = matchingCompareRecord;
          console.log(`Found matching compare record for primary key: ${normalizedSourceValue}`);
        } else {
          console.log(`No matching compare record found for primary key: ${normalizedSourceValue}`);
          // Fallback to index-based if no value match found, or use empty object
          compareRecord = {};
        }
      } else {
        // Fallback to index-based matching if primary key value not found in main record
        console.log('SURROGATE_KEY value not found in main record, using index-based matching');
        compareRecord = (compareViewData.length > recordIndex) ? compareViewData[recordIndex] : {};
      }
    }

    // Helper function to format values using DataType from column metadata
    const formatValue = (value, fieldName) => {
      if (value === null || value === undefined || value === '') return '';

      console.log(`Processing field: ${fieldName}, value:`, value);

      // Find column metadata for this field from the combined metadata
      const columnMetadata = allColumnMetadata.find(col => col.fieldName === fieldName);
      const dataType = columnMetadata?.DataType || '';
      console.log(`Field: ${fieldName}, DataType: ${dataType}`);
      const lowerDataType = dataType.toLowerCase();

      // Format based on data type from API
      if (lowerDataType.includes('date') || lowerDataType.includes('time')) {
        try {
          console.log(`Formatting date field: ${fieldName}, value: ${value}, format: ${dateFormat}`);
          
          let dateValue = value;
          // For static dates from documents (YYYY-MM-DD), avoid timezone conversion
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [y, m, d] = value.split('-').map(Number);
            dateValue = new Date(y, m - 1, d);
          }
          
          const formattedDate = formatDate(dateValue, dateFormat);
          console.log(`Formatted date for ${fieldName}:`, formattedDate);
          return formattedDate;
        } catch (e) {
          console.warn(`Error formatting date field ${fieldName}:`, e, 'Value:', value, 'Format:', dateFormat);
          return value; // Return original value if formatting fails
        }
      }
      // Format numeric fields based on data type
      else if (!isTextIdentifierField(fieldName) &&
        (lowerDataType.includes('int') ||
        lowerDataType.includes('num') ||
        lowerDataType.includes('decimal') ||
        lowerDataType.includes('float') ||
        lowerDataType.includes('double'))) {
        try {
          const numericValue = typeof value === 'string' && isGridNumericCellValue(value.replace(/,/g, ''))
            ? parseGridNumericCellValue(value.replace(/,/g, ''))
            : typeof value === 'number'
            ? value
            : isGridNumericCellValue(value)
            ? parseGridNumericCellValue(value)
            : NaN;

          if (!isNaN(numericValue)) {
            return formatAmount(numericValue, amountFormat);
          }
        } catch (e) {
          console.warn(`Error formatting number field ${fieldName}:`, e);
        }
      }

      return String(value);
    };

    // Create grid data
    return fieldArray?.map((fieldName, index) => {
      const sourceColName = sourceColMap[fieldName];
      const compareColName = compareColMap[fieldName];
      const resultColName = resultColMap[fieldName];

      const sourceValue = sourceColName ? mainRecord[sourceColName] : '';
      const compareValue = compareColName ? compareRecord[compareColName] : '';
      const resultValue = resultColName ? mainRecord[resultColName] : '';

      return {
        id: `compare_row_${index}`,
        field: fieldName,
        source_columns: formatValue(sourceValue, fieldName),
        compare_columns: formatValue(compareValue, fieldName),
        result_columns: formatValue(resultValue, fieldName)
      };
    }) || [];
  };


  // Helper function to normalize field names for matching
  const normalizeFieldName = (fieldName) => {
    if (!fieldName) return '';
    return fieldName
      .toUpperCase()
      .replace(/[_\s-]/g, '_')
      .replace(/__+/g, '_');
  };

  // Helper function to match API field names to form field names
  const matchFieldName = (formFieldName, apiFieldNames) => {
    const normalizedFormField = normalizeFieldName(formFieldName);
    return apiFieldNames.find(apiField => {
      const normalizedApiField = normalizeFieldName(apiField);
      return normalizedFormField === normalizedApiField ||
        normalizedFormField.includes(normalizedApiField) ||
        normalizedApiField.includes(normalizedFormField);
    });
  };

  const fetchDropdownOptionsForStep = async (stepName) => {
    const currentStep = steps[activeStep];
    const isSOIStep = currentStep && currentStepDetails && currentStepDetails.payload;

    // Also check if viewColumns have EditType: "Dropdown" fields (new API-based approach)
    const hasDropdownEditType = viewColumns && viewColumns.some(col =>
      col.editable === true && col.EditType === 'Dropdown'
    );

    console.log('fetchDropdownOptionsForStep called:', {
      stepName,
      isSOIStep,
      hasDropdownEditType,
      hasCurrentStepDetails: !!currentStepDetails,
      viewColumnsLength: viewColumns?.length,
    });

    // Check if this is a Schedule of Investment step (has payload structure) OR has dropdown EditType fields
    if (isSOIStep || hasDropdownEditType) {
      try {
        setDropdownLoading(true);
        const token = localStorage.getItem('token');
        const tenant = localStorage.getItem('tenant_integration');
        // const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/external-soi/dropdowns`;
        const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/external-soi/dropdowns?tenant=${encodeURIComponent(tenant || '')}`;

        console.log('Fetching SOI dropdown options from:', apiUrl);

        const resp = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (resp.ok) {
          const data = await resp.json();

          // Get editable fields from viewColumns
          const editableFields = viewColumns.filter(col => col.editable === true);

          if (editableFields.length === 0) {
            setFieldDropdownOptions({});
            return;
          }

          // Map API response fields to form fields
          const dropdowns = {};
          const apiFieldNames = Object.keys(data);

          editableFields.forEach(col => {
            const matchedApiField = matchFieldName(col.name, apiFieldNames);
            if (matchedApiField && data[matchedApiField] && Array.isArray(data[matchedApiField])) {
              dropdowns[col.name] = data[matchedApiField];
            }
          });

          setFieldDropdownOptions(dropdowns);
        } else {
          console.error('Failed to fetch SOI dropdown options:', resp.status);
          setFieldDropdownOptions({});
        }
      } catch (e) {
        console.error('Error fetching SOI dropdown options:', e);
        setFieldDropdownOptions({});
      } finally {
        setDropdownLoading(false);
      }
      return;
    }

    // Original logic for GP, Fund, Investors steps
    let apiUrl = null;
    let fieldKey = null;
    if (stepName === 'GP') {
      apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/gp/by_process_id`;
      fieldKey = 'INVESTOR_NAME';
    } else if (stepName === 'Investors') {
      apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/investor/by_process_id`;
      fieldKey = 'INVESTOR_NAME';
    } else if (stepName === 'Fund') {
      apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/funds`;
      fieldKey = 'FUND_NAME';
    }
    if (!apiUrl || !fieldKey) return;

    // Add process_id parameter for GP and Investors steps
    if ((stepName === 'GP' || stepName === 'Investors') && workflowInstanceId) {
      apiUrl += `?process_id=${workflowInstanceId}`;
    }

    // Add tenant_id parameter if available
    const tenantId = localStorage.getItem('tenant_integration');
    if (tenantId) {
      const separator = apiUrl.includes('?') ? '&' : '?';
      apiUrl += `${separator}tenant_id=${tenantId}`;
    }

    try {
      setDropdownLoading(true);
      const token = localStorage.getItem('token');
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        let options = [];
        const arr = data.gp || data.investors || data.funds || data.data || [];

        if (arr.length === 0) {
          showInfo(`No ${stepName.toLowerCase()} data available for this process`);
          setFieldDropdownOptions({});
          return;
        }

        options = arr.map(item => item[fieldKey]).filter(Boolean);

        if (options.length === 0) {
          showInfo(`No valid ${stepName.toLowerCase()} options found`);
          setFieldDropdownOptions({});
          return;
        }

        const editableFields = viewColumns.filter(col => col.editable === true).map(col => col.name);
        const dropdowns = {};
        editableFields.forEach(field => {
          dropdowns[field] = options;
        });
        setFieldDropdownOptions(dropdowns);
      }
    } catch (e) {
      console.error('Error fetching dropdown options:', e);
    } finally {
      setDropdownLoading(false);
    }
  };

  const handleEnterGridEditMode = async () => {
    if (!canEditWorkflowData) {
      showError('You do not have permission to edit workflow data', 'Access Denied');
      return;
    }
    if (!isCurrentStepEditable()) {
      showInfo('Edit mode is not available for this step type');
      return;
    }

    setIsGridEditMode(true);
    const rowsMap = {};
    comparisonData.forEach(row => {
      rowsMap[row.id] = { ...row };
    });
    setEditedRows(rowsMap);
    setOriginalRows(rowsMap);
    const currentStep = steps[activeStep];
    if (currentStep) {
      await fetchDropdownOptionsForStep(currentStep.stepName);
    }
  };

  const handleGridEditCancel = () => {
    setIsGridEditMode(false);
    setEditedRows({});
    setOriginalRows({});
  };

  // const handleGridEditSave = async () => {
  //   const updatedData = comparisonData.map(row =>
  //     editedRows[row.id] ? { ...editedRows[row.id] } : row
  //   );
  //   setComparisonData(updatedData);
  //   setIsGridEditMode(false);
  //   setEditedRows({});
  //   setOriginalRows({});

  //   const currentStep = steps[activeStep];
  //   if (currentStep && ['GP', 'Fund', 'Investors'].includes(currentStep.stepName)) {
  //     let apiUrl = null;
  //     let normalizeField = null;
  //     if (currentStep.stepName === 'GP') {
  //       apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/gp/alias/bulk_save`;
  //       normalizeField = 'normalize_gp_alias';
  //     } else if (currentStep.stepName === 'Fund') {
  //       apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/funds/alias/bulk_save`;
  //       normalizeField = 'normalize_fund_alias';
  //     } else if (currentStep.stepName === 'Investors') {
  //       apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/investors/alias/bulk_save`;
  //       normalizeField = 'normalize_investor_alias';
  //     }
  //     if (apiUrl && normalizeField) {
  //       try {
  //         const token = localStorage.getItem('token');
  //         const editedRowsArray = Object.values(editedRows).map(({ id, ...rest }) => toSnakeCaseKeys({ ...rest, [normalizeField]: true }));
  //         // Add tenant_id parameter if available
  //         if (tenantIntegrationId) {
  //           const separator = apiUrl.includes('?') ? '&' : '?';
  //           apiUrl += `${separator}tenant=${tenantIntegrationId}`;
  //         }

  //         const resp = await fetch(apiUrl, {
  //           method: 'POST',
  //           headers: {
  //             'Authorization': `Bearer ${token}`,
  //             'Content-Type': 'application/json',
  //           },
  //           body: JSON.stringify({ aliases: editedRowsArray }),
  //         });
  //         if (resp.ok) {
  //           showSuccess('Rows saved to server successfully');
  //         } else {
  //           const errText = await resp.text();
  //           showError(`Failed to save rows: ${errText}`);
  //         }
  //       } catch (err) {
  //         showError(`Failed to save rows: ${err.message}`);
  //       }
  //     }
  //   } else {
  //     showSuccess('Rows updated successfully');
  //   }
  // };

  // Function to parse payload structure and arrange edited data accordingly
  const arrangeDataByPayloadStructure = (editedRowsArray, payloadString, viewColumnsData, changedFields = null) => {
    if (!payloadString || !editedRowsArray || editedRowsArray.length === 0) {
      console.warn('Invalid payload or edited rows array');
      return editedRowsArray;
    }

    // Use viewColumnsData parameter or fallback to viewColumns state
    const columnsData = viewColumnsData || viewColumns;
    
    // If changedFields is provided, we'll only include record types that contain changed fields
    const changedFieldsSet = changedFields ? new Set(changedFields.map(f => f.toLowerCase())) : null;

    try {
      // The payload might be in a special format where field names are listed without values
      // Example: { "company_name", "security_type" } instead of { "company_name": "", "security_type": "" }
      // We need to handle this format by converting it to valid JSON first
      let normalizedPayloadString = payloadString.trim();

      // Replace the invalid format: { "field_name", "field_name2" } with { "field_name": "", "field_name2": "" }
      // The pattern matches quoted field names followed by comma (or end of object) inside object braces
      // We need to handle both cases: fields with commas and the last field without comma

      // Strategy: Replace all instances of "field_name", with "field_name": "",
      // This handles all fields except the last one in each object
      normalizedPayloadString = normalizedPayloadString.replace(
        /"([^"]+)",/g,
        '"$1": "",'
      );

      // Now handle the last field in each object (no trailing comma, followed by whitespace and })
      // This pattern matches: "field_name" followed by optional whitespace and closing brace
      normalizedPayloadString = normalizedPayloadString.replace(
        /"([^"]+)"(\s+)\}/g,
        '"$1": ""$2}'
      );

      // Also handle case where there's no whitespace before the closing brace
      normalizedPayloadString = normalizedPayloadString.replace(
        /"([^"]+)"\}/g,
        '"$1": ""}'
      );

      console.log('Normalized payload string:', normalizedPayloadString);

      // Parse the normalized payload JSON string
      const payload = JSON.parse(normalizedPayloadString);
      console.log('Parsed payload structure:', payload);

      // Create the result structure matching payload
      const result = {};

      // Helper function to convert column name to payload field name format
      // This function tries multiple matching strategies to find the value in the row
      const findFieldValue = (row, payloadFieldName) => {
        const rowKeys = Object.keys(row);
        if (rowKeys.length === 0) return undefined;

        // Normalize field name for comparison
        const normalizedPayload = payloadFieldName.toLowerCase().replace(/_/g, '_');
        const payloadWords = normalizedPayload.split('_').filter(w => w.length > 0);

        // Strategy 1: Try UPPER_CASE match (COMPANY_NAME) - most common in grid columns
        const upperCaseField = payloadFieldName.toUpperCase();
        if (row[upperCaseField] !== undefined) {
          return row[upperCaseField];
        }

        // Strategy 2: Try exact snake_case match (company_name)
        const snakeCaseField = payloadFieldName.toLowerCase();
        if (row[snakeCaseField] !== undefined) {
          return row[snakeCaseField];
        }

        // Strategy 3: Try exact match (case-sensitive)
        if (row[payloadFieldName] !== undefined) {
          return row[payloadFieldName];
        }

        // Strategy 4: Try case-insensitive match
        const matchingKey = rowKeys.find(key => {
          return key.toLowerCase() === payloadFieldName.toLowerCase();
        });
        if (matchingKey) {
          return row[matchingKey];
        }

        // Strategy 5: Try partial word matching (handles variations like extracted_position vs POSITION_EXTRACTED)
        // Check if all words from payload field name exist in any grid column (in any order)
        if (payloadWords.length > 0) {
          const matchingKeyByWords = rowKeys.find(key => {
            const normalizedKey = key.toLowerCase().replace(/_/g, '_');
            const keyWords = normalizedKey.split('_').filter(w => w.length > 0);

            // Check if all payload words exist in key words (handles reordering)
            const allWordsMatch = payloadWords.every(payloadWord =>
              keyWords.some(keyWord => keyWord === payloadWord || keyWord.includes(payloadWord) || payloadWord.includes(keyWord))
            );

            // Also check if all key words exist in payload words (for reverse matching)
            const reverseMatch = keyWords.every(keyWord =>
              payloadWords.some(payloadWord => payloadWord === keyWord || keyWord.includes(payloadWord) || payloadWord.includes(keyWord))
            );

            return allWordsMatch && reverseMatch;
          });

          if (matchingKeyByWords) {
            return row[matchingKeyByWords];
          }
        }

        // Strategy 6: Try fuzzy matching - find column that contains the field name or vice versa
        // This handles cases where field names are similar but not exact matches
        // Sort matches by specificity (longer, more specific matches first)
        const fuzzyMatches = rowKeys.filter(key => {
          const keyLower = key.toLowerCase();
          const payloadLower = payloadFieldName.toLowerCase();
          // Check if one contains the other
          return keyLower.includes(payloadLower) || payloadLower.includes(keyLower);
        });

        if (fuzzyMatches.length > 0) {
          // Prefer more specific (longer) matches, and prefer matches that start with the payload field name
          fuzzyMatches.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();
            const payloadLower = payloadFieldName.toLowerCase();

            // Prefer exact starts with
            const aStartsWith = aLower.startsWith(payloadLower) ? 1 : 0;
            const bStartsWith = bLower.startsWith(payloadLower) ? 1 : 0;
            if (aStartsWith !== bStartsWith) return bStartsWith - aStartsWith;

            // Then prefer longer matches (more specific)
            return b.length - a.length;
          });

          return row[fuzzyMatches[0]];
        }

        return undefined;
      };

      // Process each record type in payload (e.g., soi_records, position_records, company_records)
      const payloadKeys = Object.keys(payload);

      payloadKeys.forEach(recordType => {
        const fieldArrays = payload[recordType];

        if (!Array.isArray(fieldArrays) || fieldArrays.length === 0) {
          console.warn(`Payload ${recordType} is not a valid array, creating empty array`);
          result[recordType] = [];
          return;
        }

        // Get field names from the first item in the array
        // The payload structure defines the schema - field names are the keys
        const firstItem = fieldArrays[0];

        // Handle different payload formats
        let fieldNames = [];
        if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
          // Format: [{ "company_name": "", "security_type": "" }]
          fieldNames = Object.keys(firstItem);
        } else if (Array.isArray(firstItem)) {
          // Format: [["company_name", "security_type"]]
          fieldNames = firstItem;
        } else if (typeof firstItem === 'string') {
          // Format: ["company_name", "security_type"] - fieldArrays is the array of field names
          fieldNames = fieldArrays;
        }

        if (fieldNames.length === 0) {
          console.warn(`No field names found in payload ${recordType}, creating empty array`);
          result[recordType] = [];
          return;
        }

        // If changedFields is provided, check if this record type contains any changed fields
        if (changedFieldsSet) {
          // Also check against viewColumns to handle field name variations
          const hasChangedField = fieldNames.some(fieldName => {
            const fieldLower = fieldName.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
            
            // Check if any changed field matches this field (case-insensitive, handle variations)
            const directMatch = Array.from(changedFieldsSet).some(changedField => {
              const changedLower = changedField.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
              return fieldLower === changedLower ||
                     fieldLower.includes(changedLower) ||
                     changedLower.includes(fieldLower);
            });
            
            if (directMatch) return true;
            
            // Also check if any column name matches the changed field
            const columnMatch = columnsData.some(col => {
              const colName = (col.name || '').toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
              const fieldNameLower = fieldName.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
              
              // Check if column name matches the payload field name
              if (colName === fieldNameLower || colName.includes(fieldNameLower) || fieldNameLower.includes(colName)) {
                // Now check if this column name matches any changed field
                return Array.from(changedFieldsSet).some(changedField => {
                  const changedLower = changedField.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
                  return colName === changedLower ||
                         colName.includes(changedLower) ||
                         changedLower.includes(colName);
                });
              }
              return false;
            });
            
            return columnMatch;
          });
          
          // Skip this record type if it doesn't contain any changed fields
          if (!hasChangedField) {
            console.log(`Skipping ${recordType} - no changed fields in this record type`);
            result[recordType] = [];
            return;
          }
        }

        console.log(`Processing ${recordType} with fields:`, fieldNames);

        // Process ALL record types dynamically based on payload structure
        // For each record type, map all edited rows and extract the required fields
        result[recordType] = editedRowsArray.map((row, index) => {
          const record = {};
          // Process fields in the order they appear in payload
          fieldNames.forEach(fieldName => {
            let value = findFieldValue(row, fieldName);

            // If field not found, try to find it using column metadata mappings
            if (value === undefined) {
              // Try to find a column in viewColumns that matches this field name
              const matchingColumn = columnsData.find(col => {
                const colName = (col.name || '').toLowerCase().trim();
                const fieldLower = fieldName.toLowerCase().trim();
                // Check exact match or if field name is contained in column name or vice versa
                return colName === fieldLower ||
                  colName.replace(/_/g, '').replace(/\s/g, '') === fieldLower.replace(/_/g, '').replace(/\s/g, '') ||
                  colName.includes(fieldLower) ||
                  fieldLower.includes(colName);
              });

              if (matchingColumn && matchingColumn.name) {
                // Try to find the value using the column's actual name from the grid
                value = findFieldValue(row, matchingColumn.name);
                if (value !== undefined) {
                  console.log(`Found field "${fieldName}" using column mapping "${matchingColumn.name}":`, value);
                }
              }

              // If still not found, try fuzzy matching on all row keys
              if (value === undefined) {
                const rowKeys = Object.keys(row);
                const fieldLower = fieldName.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
                const similarKeys = rowKeys.filter(key => {
                  const keyLower = key.toLowerCase().trim().replace(/_/g, '').replace(/\s/g, '');
                  // More aggressive matching: check if all significant parts match
                  return keyLower === fieldLower ||
                    keyLower.includes(fieldLower) ||
                    fieldLower.includes(keyLower) ||
                    (keyLower.length > 3 && fieldLower.length > 3 &&
                      keyLower.slice(0, 4) === fieldLower.slice(0, 4));
                });

                if (similarKeys.length > 0) {
                  // Prefer exact case-insensitive match, then longest match
                  similarKeys.sort((a, b) => {
                    const aLower = a.toLowerCase();
                    const bLower = b.toLowerCase();
                    const fieldLower = fieldName.toLowerCase();
                    if (aLower === fieldLower && bLower !== fieldLower) return -1;
                    if (bLower === fieldLower && aLower !== fieldLower) return 1;
                    return b.length - a.length;
                  });
                  value = row[similarKeys[0]];
                  console.log(`Found field "${fieldName}" using fuzzy match "${similarKeys[0]}":`, value);
                }
              }

              // Final fallback: log warning with available keys for debugging
              if (value === undefined && index === 0) {
                console.warn(`Field "${fieldName}" not found in row. Available keys:`, Object.keys(row).slice(0, 15));
              }
            }

            // Debug: Log field name and value for first row only
            if (index === 0) {
              console.log(`[${recordType}] Field: ${fieldName}, Value: ${value}, Type: ${typeof value}`);
            }

            // Convert value based on field type from viewColumns
            // Only convert if we have a value (undefined means field not found, which is handled above)
            if (value !== undefined && value !== null && value !== '') {
              // Find the column configuration to determine data type
              const columnConfig = columnsData.find(col => {
                const colName = col.name || '';
                return colName.toLowerCase() === fieldName.toLowerCase() ||
                  colName.toUpperCase() === fieldName.toUpperCase();
              });

              const dataType = columnConfig?.DataType || '';
              const editType = columnConfig?.EditType;
              const lowerDataType = dataType.toLowerCase();

              // Handle date fields - convert to ISO format (YYYY-MM-DD)
              if (dataType === 'Date' || lowerDataType.includes('date') || fieldName.toLowerCase().includes('date')) {
                if (typeof value === 'string') {
                  // Try to parse common date formats and convert to ISO
                  // Handle MM/DD/YYYY format
                  const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (dateMatch) {
                    const [, month, day, year] = dateMatch;
                    value = `${year}-${month}-${day}`;
                  } else {
                    // Try to parse as date and convert to ISO
                    try {
                      const date = new Date(value);
                      if (!isNaN(date.getTime())) {
                        value = date.toISOString().split('T')[0]; // YYYY-MM-DD format
                      }
                    } catch (e) {
                      console.warn(`Failed to parse date value: ${value}`, e);
                    }
                  }
                }
              }
              // Handle number fields - remove commas and convert to number
              else if (dataType === 'Number' ||
                lowerDataType.includes('int') ||
                lowerDataType.includes('num') ||
                lowerDataType.includes('decimal') ||
                lowerDataType.includes('float') ||
                lowerDataType.includes('double') ||
                fieldName.toLowerCase().includes('shares') ||
                fieldName.toLowerCase().includes('cost') ||
                fieldName.toLowerCase().includes('value') ||
                fieldName.toLowerCase().includes('percent')) {
                if (editType === 'Dropdown') {
                  // For dropdowns with Number DataType:
                  // If value is already a number, use it
                  // If value is a string that's a pure number (like "20"), convert to number
                  // If value is a string label (like "20VC Explorer..."), keep as string
                  // The backend API should handle the conversion or we need a lookup mechanism
                  if (typeof value === 'number') {
                    // Already a number, use as-is
                    value = value;
                  } else if (typeof value === 'string') {
                    const trimmedValue = value.trim();
                    const numValue = Number(trimmedValue);
                    // Check if it's a pure number string (e.g., "20" not "20VC Explorer...")
                    if (!isNaN(numValue) && String(numValue) === trimmedValue) {
                      // It's a pure number string, convert to number
                      value = numValue;
                    }
                    // Otherwise keep as string (it's a label, backend should handle it)
                  }
                } else {
                  // For non-dropdown number fields, convert to number
                  if (typeof value === 'string') {
                    // Remove commas and parse as number
                    const cleanValue = value.replace(/,/g, '').trim();
                    // Only try to parse if the string is not empty after cleaning
                    if (cleanValue !== '') {
                      const numValue = parseFloat(cleanValue);
                      if (!isNaN(numValue) && isFinite(numValue)) {
                        value = numValue;
                      } else {
                        // If parsing fails, log warning but preserve original value
                        // Don't set to null - let the API handle validation
                        console.warn(`⚠️ Failed to parse number for field "${fieldName}": "${value}". Keeping original value.`);
                        // Keep the original value as string - don't convert to null
                      }
                    }
                    // If cleanValue is empty, keep value as empty string (don't convert to null)
                  } else if (typeof value === 'number') {
                    // Already a number, use as-is
                    value = value;
                  }
                }
                // If value is already null or undefined, leave it as-is (don't process)
              }
              // Handle string fields - ensure they're strings
              else if (dataType === 'String' || lowerDataType.includes('string') || lowerDataType.includes('text')) {
                // Ensure it's a string
                if (typeof value === 'number') {
                  value = String(value);
                } else if (value !== null && value !== undefined) {
                  value = String(value);
                }
              }
            }
            // Note: We don't set value to null here if undefined - we keep it undefined
            // This allows us to distinguish between "field not found" vs "field is null/empty"

            // Set the field value
            // - If value is undefined, it means the field doesn't exist in the row, set to null
            // - If value is null or empty string, preserve it (null is valid, empty string might be valid)
            // - Otherwise, use the processed value
            if (value === undefined) {
              // Field not found - this is a problem, log it for debugging
              if (index === 0) {
                console.warn(`⚠️ Field "${fieldName}" not found in any row keys. Setting to null.`);
              }
              record[fieldName] = null;
            } else {
              // Field found (even if null or empty) - use the processed value
              record[fieldName] = value;
            }
          });
          return record;
        });
      });

      console.log('Arranged payload data:', result);
      return result;
    } catch (error) {
      console.error('Error parsing payload structure:', error);
      console.error('Payload string:', payloadString);
      // If payload parsing fails, return the edited rows as-is
      return editedRowsArray;
    }
  };

  const currentWorkflowStepName = () => {
    const currentStep = steps[activeStep];
    return currentStep?.stepName || currentStep?.name || 'Workflow';
  };

  const resolveHistoricalFundName = () => {
    const fromFile = fileInstanceDetails?.fundName || fileInstanceDetails?.fund_name;
    if (fromFile) return fromFile;
    const rows = Array.isArray(comparisonData) ? comparisonData : [];
    const row = rows.find((item) =>
      item?.FUND || item?.FUND_NAME || item?.REFERENCE_FUND_NAME || item?.PROVIDED_FUND_NAME
    );
    if (!row) return '';
    return row.FUND || row.FUND_NAME || row.REFERENCE_FUND_NAME || row.PROVIDED_FUND_NAME || '';
  };

  const collectHistoricalChanges = () => {
    const changes = [];
    Object.keys(editedRows || {}).forEach((rowId) => {
      const editedRow = editedRows[rowId] || {};
      const originalRow = originalRows?.[rowId] || {};
      const rowIndex = comparisonData.findIndex((row) => String(row.id) === String(rowId));
      const rowLabel = `Record ${rowIndex >= 0 ? rowIndex + 1 : rowId}`;
      Object.keys(editedRow).forEach((field) => {
        if (field === 'id') return;
        const upper = String(field).toUpperCase();
        if (upper.endsWith('_VALIDATION') || upper.includes('NUMBER_OF_FAILURES')) return;
        // Company and position edits also copy values into these fields for the save payload.
        // They are not separate edits, so keep them out of Historical Activity.
        const normalized = String(field).trim().toLowerCase().replace(/\s+/g, '_');
        if (normalized === 'matching' || normalized === 'extracted_company' || normalized === 'extracted_position') return;
        const originalValue = originalRow[field];
        const editedValue = editedRow[field];
        const originalVal = originalValue === null || originalValue === undefined ? '' : String(originalValue);
        const editedVal = editedValue === null || editedValue === undefined ? '' : String(editedValue);
        if (originalVal !== editedVal) {
          changes.push({
            rowLabel,
            field,
            oldValue: originalVal,
            newValue: editedVal,
          });
        }
      });
    });
    return changes;
  };

  const recordStepEdit = async () => {
    const stepName = currentWorkflowStepName();
    if (String(stepName).trim().toLowerCase() === 'classification') return;
    const changes = collectHistoricalChanges();
    if (changes.length === 0) return;
    try {
      const saved = await recordWorkflowHistoricalActivity({
        fileInstanceId,
        workflowName: selectedDocumentType,
        fundName: resolveHistoricalFundName(),
        fileName,
        stepName,
        action: 'Edits Saved',
        changes,
      });
      if (saved) setHistoryRefreshKey((key) => key + 1);
    } catch (error) {
      console.error('Error recording historical activity:', error);
    }
  };

  const recordStepApproval = async () => {
    const stepName = currentWorkflowStepName();
    try {
      const saved = await recordWorkflowHistoricalActivity({
        fileInstanceId,
        workflowName: selectedDocumentType,
        fundName: resolveHistoricalFundName(),
        fileName,
        stepName,
        action: 'Approved',
        changes: [],
      });
      if (saved) setHistoryRefreshKey((key) => key + 1);
    } catch (error) {
      console.error('Error recording historical activity:', error);
    }
  };

  const handleGridEditSave = async () => {
    if (!canEditWorkflowData) {
      showError('You do not have permission to edit workflow data', 'Access Denied');
      return;
    }
    const updatedData = comparisonData.map(row =>
      editedRows[row.id] ? { ...editedRows[row.id] } : row
    );

    // Get the current step details for logging
    const currentStep = steps[activeStep];

    // Log the edit action
    try {
      await AuditLoggers.custom(
        `User edited ${currentStep?.name || 'a workflow step'} with ${Object.keys(editedRows).length} rows changed`,
        'Workflow Step Edited',
        'handleGridEditSave',
        {
          workflowInstanceId,
          workflowId: selectedWorkflowId || 'unknown',
          stepId: currentStep?.id || 'unknown',
          stepName: currentStep?.name || 'unknown',
          rowsEdited: Object.keys(editedRows).length,
          fileInstanceId,
          fileName,
          workflowDefinitionSnowflakeId
        }
      );
    } catch (error) {
      console.error('Error logging workflow step edit:', error);
      // Don't block the save operation if logging fails
    }
    setComparisonData(updatedData);
    setIsGridEditMode(false);
    setEditedRows({});
    setOriginalRows({});

    // Handle GP, Fund, Investors steps (existing functionality)
    if (currentStep && ['GP', 'Fund', 'Investors'].includes(currentStep.stepName)) {
      let apiUrl = null;
      if (currentStep.stepName === 'GP') {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/gp/alias/bulk_save`;
      } else if (currentStep.stepName === 'Fund') {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/funds/alias/bulk_save`;
      } else if (currentStep.stepName === 'Investors') {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/investors/alias/bulk_save`;
      }

      if (apiUrl) {
        // Show dialog immediately with "Saving..." status
        setMaterializationDialogOpen(true);
        setMaterializationStatus('Saving changes...');
        setMaterializationLoading(true);

        try {
          const token = localStorage.getItem('token');
          // ✅ REMOVED: normalize field is no longer added
          const editedRowsArray = Object.values(editedRows).map(({ id, ...rest }) => toSnakeCaseKeys(rest));

          // Add tenant_id parameter if available
          if (tenantIntegrationId) {
            const separator = apiUrl.includes('?') ? '&' : '?';
            apiUrl += `${separator}tenant=${tenantIntegrationId}`;
          }

          const resp = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ aliases: editedRowsArray }),
          });
          if (resp.ok) {
            showSuccess('Rows saved successfully');
            await recordStepEdit();
            // Update status to re-calculating
            setMaterializationStatus('Re-Calculating tables...');
            // Trigger materialization after successful save
            await handleMaterializeTables(true);
          } else {
            const errText = await resp.text();
            showError(`Failed to save rows: ${errText}`);
            setMaterializationStatus(`Failed to save: ${errText}`);
            setMaterializationLoading(false);
          }
        } catch (err) {
          showError(`Failed to save rows: ${err.message}`);
          setMaterializationStatus(`Failed to save: ${err.message}`);
          setMaterializationLoading(false);
        }
      }
    }
    // Handle steps with payload structure (new functionality for Schedule of Investments, etc.)
    else if (currentStep && currentStepDetails && currentStepDetails.payload) {
      try {
        // Show dialog immediately with "Saving..." status
        setMaterializationDialogOpen(true);
        setMaterializationStatus('Saving changes...');
        setMaterializationLoading(true);

        const token = localStorage.getItem('token');
        
        // Determine which fields were actually changed by comparing with originalRows
        // Also filter to only include rows that actually have changes
        const changedFields = [];
        const actuallyChangedRows = [];
        
        Object.keys(editedRows).forEach(rowId => {
          const editedRow = editedRows[rowId];
          const originalRow = originalRows[rowId];
          let rowHasChanges = false;
          const rowChangedFields = [];
          
          if (originalRow) {
            // Compare all fields to find what changed
            Object.keys(editedRow).forEach(field => {
              // Skip id field
              if (field === 'id') return;
              
              const originalValue = originalRow[field];
              const editedValue = editedRow[field];
              
              // Check if value actually changed (handle null/undefined/empty string differences)
              const originalVal = originalValue === null || originalValue === undefined ? '' : String(originalValue);
              const editedVal = editedValue === null || editedValue === undefined ? '' : String(editedValue);
              
              if (originalVal !== editedVal) {
                rowHasChanges = true;
                rowChangedFields.push(field);
                if (!changedFields.includes(field)) {
                  changedFields.push(field);
                }
              }
            });
          } else {
            // If original row not found, assume all fields in edited row are changed
            rowHasChanges = true;
            Object.keys(editedRow).forEach(field => {
              if (field !== 'id') {
                rowChangedFields.push(field);
                if (!changedFields.includes(field)) {
                  changedFields.push(field);
                }
              }
            });
          }
          
          // Only include this row if it has actual changes
          if (rowHasChanges) {
            console.log(`✅ Row ${rowId} has changes in fields:`, rowChangedFields);
            const { id, ...rest } = editedRow;
            actuallyChangedRows.push(rest);
          } else {
            console.log(`⏭️ Row ${rowId} has no changes, skipping`);
          }
        });

        const editedRowsArray = actuallyChangedRows;

        console.log('📊 Changed fields detected:', changedFields);
        console.log('📊 Total edited rows (before filtering):', Object.keys(editedRows).length);
        console.log('📊 Total actually changed rows (after filtering):', editedRowsArray.length);

        // Debug: Log first row to see what data we're working with
        if (editedRowsArray.length > 0) {
          console.log('📊 First row data being processed:', editedRowsArray[0]);
          console.log('📊 Available fields in first row:', Object.keys(editedRowsArray[0]));
        } else {
          console.log('⚠️ No rows with actual changes detected. All editedRows:', Object.keys(editedRows));
          // If no rows have changes, show a message and return early
          showInfo('No changes detected. Please make edits before saving.');
          setMaterializationDialogOpen(false);
          setMaterializationLoading(false);
          return;
        }

        // Arrange data according to payload structure, only including record types with changed fields
        console.log('📤 Passing to arrangeDataByPayloadStructure:', {
          rowCount: editedRowsArray.length,
          changedFields: changedFields,
          sampleRow: editedRowsArray[0]
        });
        const payloadData = arrangeDataByPayloadStructure(editedRowsArray, currentStepDetails.payload, viewColumns, changedFields);
        console.log('📥 Payload data created:', {
          recordTypes: Object.keys(payloadData),
          recordCounts: Object.keys(payloadData).reduce((acc, key) => {
            acc[key] = Array.isArray(payloadData[key]) ? payloadData[key].length : 0;
            return acc;
          }, {})
        });

        // Validate that payloadData is properly structured (should be an object, not an array)
        if (!payloadData || Array.isArray(payloadData)) {
          throw new Error('Failed to structure data according to payload format. Please check the payload configuration.');
        }

        // Build API URL for external SOI bulk save
        const tenantId = localStorage.getItem('tenant_integration');
        if (!tenantId) {
          throw new Error('Tenant ID not found. Please ensure you are logged in.');
        }
        const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/external-soi-bulk/workflow/external-soi/bulk-all?tenant=${tenantId}`;

        console.log('Saving payload data:', payloadData);
        console.log('API URL:', apiUrl);

        const resp = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payloadData),
        });

        if (resp.ok) {
          const responseData = await resp.json().catch(() => null);
          console.log('Save response:', responseData);
          showSuccess('Rows saved successfully');
          await recordStepEdit();
          // Update status to re-calculating
          setMaterializationStatus('Re-Calculating tables...');
          // Trigger materialization after successful save
          await handleMaterializeTables(true);
        } else {
          const errText = await resp.text();
          console.error('Save error response:', errText);
          showError(`Failed to save rows: ${errText}`);
          setMaterializationStatus(`Failed to save: ${errText}`);
          setMaterializationLoading(false);
        }
      } catch (err) {
        showError(`Failed to save rows: ${err.message}`);
        setMaterializationStatus(`Failed to save: ${err.message}`);
        setMaterializationLoading(false);
      }
    }
    // For other steps, just update locally
    else {
      showSuccess('Rows updated successfully');
    }
  };

  // Optimized TextField component for SOI steps with local state
  const SOITextField = React.memo(({ rowId, field, value: externalValue, onChange }) => {
    const [localValue, setLocalValue] = useState(externalValue || '');
    const inputRef = useRef(null);
    const isFocusedRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const lastExternalValueRef = useRef(externalValue);
    const shouldRestoreFocusRef = useRef(false);

    // Keep onChange ref updated but don't cause re-renders
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    // Only sync with external value when not focused AND value actually changed
    useEffect(() => {
      if (!isFocusedRef.current && externalValue !== lastExternalValueRef.current) {
        setLocalValue(externalValue || '');
        lastExternalValueRef.current = externalValue;
      }

      // Restore focus if we lost it during a re-render
      if (shouldRestoreFocusRef.current && inputRef.current) {
        requestAnimationFrame(() => {
          if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
            // Move cursor to end
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
          shouldRestoreFocusRef.current = false;
        });
      }
    }, [externalValue]);

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const handleChange = (event) => {
      const newValue = event.target.value;
      setLocalValue(newValue); // Update immediately for smooth typing - this is all we need for UI

      // Mark that we should restore focus if lost during re-render
      if (isFocusedRef.current) {
        shouldRestoreFocusRef.current = true;
      }

      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Don't update parent state while typing - only on blur
      // This prevents the blocking re-renders that freeze the cursor
      // The local state is what the user sees, parent state will sync on blur
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
      shouldRestoreFocusRef.current = false;

      // Clear any pending debounced call
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Immediately sync on blur to ensure value is saved
      // Use requestIdleCallback to make this non-blocking
      const syncValue = () => {
        if (localValue !== (externalValue || '')) {
          if (onChangeRef.current) {
            onChangeRef.current(rowId, field, localValue);
          }
        }
        lastExternalValueRef.current = localValue;
      };

      // Schedule update in next idle period to avoid blocking
      if (window.requestIdleCallback) {
        window.requestIdleCallback(syncValue, { timeout: 100 });
      } else {
        setTimeout(syncValue, 0);
      }
    };

    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        variant="outlined"
        value={localValue}
        onChange={handleChange}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
            (e.key === 'Enter' && !e.shiftKey)) {
            e.preventDefault();
          }
        }}
        onKeyUp={(e) => {
          e.stopPropagation();
        }}
        onKeyPress={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.currentTarget.focus();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onFocus={(e) => {
          e.stopPropagation();
          isFocusedRef.current = true;
          shouldRestoreFocusRef.current = false;
          // Prevent any pending syncs while focused
          lastExternalValueRef.current = localValue;
        }}
        onBlur={handleBlur}
        autoFocus={false}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '0.7rem',
            height: '32px',
          },
          '& .MuiInputBase-input': {
            fontSize: '0.7rem',
            padding: '4px 8px',
          },
        }}
      />
    );
  }, (prevProps, nextProps) => {
    // Only re-render if rowId, field, or value actually changed
    // Ignore onChange changes to prevent unnecessary re-renders
    return prevProps.rowId === nextProps.rowId &&
      prevProps.field === nextProps.field &&
      prevProps.value === nextProps.value;
  });
  SOITextField.displayName = 'SOITextField';

  const handleGridCellChange = (rowId, field, value) => {
    const currentStep = steps[activeStep];
    const isSOIStep = currentStep && currentStepDetails && currentStepDetails.payload;

    // Get column configuration to check DataType and EditType
    const colConfig = viewColumns.find(vc => vc.name === field);
    const dataType = colConfig?.DataType;
    const editType = colConfig?.EditType;
    const isDropdownField = colConfig && colConfig.editable && editType === 'Dropdown';

    // Process value based on DataType and EditType
    let processedValue = value;

    if (isDropdownField) {
      // For dropdowns, store as string for display, but we'll convert based on DataType when saving
      processedValue = value ? String(value) : '';
    } else if (dataType === 'Number') {
      // For number fields (non-dropdown), convert to number if possible
      if (value === '' || value === null || value === undefined) {
        processedValue = null;
      } else {
        const numValue = Number(value);
        processedValue = isNaN(numValue) ? value : numValue;
      }
    } else if (dataType === 'Date') {
      // For date fields, ensure proper format (already handled by date picker)
      processedValue = value || '';
    } else {
      // For string fields or unknown types, keep as string
      processedValue = value ? String(value) : '';
    }

    setEditedRows(prev => {
      const updatedRow = {
        ...prev[rowId],
        [field]: processedValue
      };

      // SOI step specific logic: capture previous values for company and position fields
      if (isSOIStep) {
        const fieldLower = field.toLowerCase().trim();
        const originalRow = originalRows[rowId];

        // Helper function to find field value in original row (case-insensitive, handles variations)
        const findOriginalFieldValue = (fieldName) => {
          if (!originalRow) return '';

          const fieldNameLower = fieldName.toLowerCase().trim();
          const rowKeys = Object.keys(originalRow);

          // Try exact match first
          if (originalRow[field] !== undefined && originalRow[field] !== null) {
            return originalRow[field];
          }

          // Try case-insensitive match
          const matchingKey = rowKeys.find(key =>
            key.toLowerCase().trim() === fieldNameLower ||
            key.toLowerCase().trim() === fieldNameLower.replace(/_/g, '_')
          );
          if (matchingKey && originalRow[matchingKey] !== undefined && originalRow[matchingKey] !== null) {
            return originalRow[matchingKey];
          }

          // Try common variations
          const variations = [
            fieldName.toUpperCase(),
            fieldName.toUpperCase().replace(/_/g, '_'),
            `${fieldName.toUpperCase()}_NAME`,
            `EXTRACTED_${fieldName.toUpperCase()}`,
          ];

          for (const variation of variations) {
            if (originalRow[variation] !== undefined && originalRow[variation] !== null) {
              return originalRow[variation];
            }
          }

          return '';
        };

        // Handle company field: store previous value in extracted_company and new value in matching
        if (fieldLower === 'company' || fieldLower.includes('company')) {
          const previousValue = findOriginalFieldValue('company');

          // Store previous value in extracted_company
          updatedRow.extracted_company = previousValue;

          // Store new value in matching field (if it exists in payload structure)
          // The matching field will be picked up by arrangeDataByPayloadStructure
          updatedRow.matching = value;
        }

        // Handle position field: store previous value in extracted_position
        if (fieldLower === 'position' || fieldLower.includes('position')) {
          const previousValue = findOriginalFieldValue('position');

          // Store previous value in extracted_position
          updatedRow.extracted_position = previousValue;
        }
      }

      return {
        ...prev,
        [rowId]: updatedRow
      };
    });
  };

  const getGridColumnsWithEdit = () => {
    const currentStep = steps[activeStep];
    const isSOIStep = currentStep && currentStepDetails && currentStepDetails.payload;
    const isDropdownStep = currentStep && (
      ['GP', 'Fund', 'Investors'].includes(currentStep.stepName) || isSOIStep
    );

    const baseColumns = dynamicColumns.map(col => {
      const colConfig = viewColumns.find(vc => vc.name === col.field);
      const isEditable = !!(colConfig && colConfig.editable);
      const editType = colConfig?.EditType; // Get EditType from column config
      const dropdownOptions = fieldDropdownOptions[col.field] || [];
      return {
        ...col,
        width: col.width || 250,   // ✅ fixed width prevents vibration
        flex: undefined,           // remove flex from editable columns
        renderCell: (params) => {
          if (isGridEditMode && isEditable) {
            // Priority 1: Use EditType from API if available
            if (editType) {
              // Date Picker
              if (editType === 'Date') {
                const cellValue = editedRows[params.id]?.[col.field] || '';
                // Convert date to YYYY-MM-DD format for HTML5 date input
                let dateInputValue = '';
                if (cellValue) {
                  try {
                    const date = new Date(cellValue);
                    if (!isNaN(date.getTime())) {
                      dateInputValue = date.toISOString().split('T')[0];
                    }
                  } catch (e) {
                    // If parsing fails, try to extract YYYY-MM-DD from string
                    const match = String(cellValue).match(/(\d{4}-\d{2}-\d{2})/);
                    if (match) {
                      dateInputValue = match[1];
                    }
                  }
                }

                return (
                  <Box
                    sx={{ width: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={dateInputValue}
                      onChange={(event) => {
                        handleGridCellChange(params.id, col.field, event.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
                          e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                          (e.key === 'Enter' && !e.shiftKey)) {
                          e.preventDefault();
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.currentTarget.focus();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.7rem',
                          height: '32px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.7rem',
                          padding: '4px 8px',
                        },
                      }}
                    />
                  </Box>
                );
              }

              // Dropdown/Autocomplete
              if (editType === 'Dropdown') {
                return (
                  <Box
                    sx={{ width: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={dropdownOptions}
                      value={String(editedRows[params.id]?.[col.field] || '')}
                      onChange={(event, newValue) => {
                        // Ensure we always store the string value, not the index
                        const stringValue = newValue ? String(newValue) : '';
                        handleGridCellChange(params.id, col.field, stringValue);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        // Ensure proper matching by comparing strings
                        return String(option) === String(value);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      renderInput={(inputParams) => (
                        <TextField
                          {...inputParams}
                          variant="outlined"
                          size="small"
                          fullWidth
                          onKeyDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.7rem',
                              height: '32px',
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '0.7rem',
                              padding: '4px 8px',
                            },
                          }}
                        />
                      )}
                      PopperProps={{
                        sx: {
                          width: 'auto !important',
                          minWidth: '250px !important',
                          maxWidth: '400px !important',
                        },
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                              boundary: 'viewport',
                            },
                          },
                        ],
                      }}
                    />
                  </Box>
                );
              }

              // Text Field (EditType === 'Text' or any other value)
              if (editType === 'Text' || !['Date', 'Dropdown'].includes(editType)) {
                // For SOI steps, use optimized TextField with local state
                if (isSOIStep) {
                  return (
                    <Box
                      sx={{ width: '100%' }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <SOITextField
                        rowId={params.id}
                        field={col.field}
                        value={editedRows[params.id]?.[col.field] || ''}
                        onChange={handleGridCellChange}
                      />
                    </Box>
                  );
                }

                // For non-SOI steps, use regular TextField
                return (
                  <Box
                    sx={{ width: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={editedRows[params.id]?.[col.field] || ''}
                      onChange={(event) =>
                        handleGridCellChange(params.id, col.field, event.target.value)
                      }
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
                          e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                          (e.key === 'Enter' && !e.shiftKey)) {
                          e.preventDefault();
                        }
                      }}
                      onKeyUp={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyPress={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.currentTarget.focus();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                      }}
                      autoFocus={false}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.7rem',
                          height: '32px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.7rem',
                          padding: '4px 8px',
                        },
                      }}
                    />
                  </Box>
                );
              }
            }

            // Priority 2: Fallback to old logic for backward compatibility
            // For GP/Fund/Investors: Always show Autocomplete (even if empty)
            // For SOI: Show Autocomplete only if options exist, otherwise TextField
            const isGPFundInvestors = currentStep && ['GP', 'Fund', 'Investors'].includes(currentStep.stepName);
            const shouldShowAutocomplete = isGPFundInvestors || (isSOIStep && dropdownOptions.length > 0);

            if (isDropdownStep && shouldShowAutocomplete) {
              return (
                <Box
                  sx={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={dropdownOptions}
                    value={String(editedRows[params.id]?.[col.field] || '')}
                    onChange={(event, newValue) => {
                      // Ensure we always store the string value, not the index
                      const stringValue = newValue ? String(newValue) : '';
                      handleGridCellChange(params.id, col.field, stringValue);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      // Ensure proper matching by comparing strings
                      return String(option) === String(value);
                    }}
                    onKeyDown={(e) => {
                      // Stop propagation to prevent grid navigation
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      // Stop click propagation to prevent cell selection
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      // Stop mousedown propagation to prevent cell selection
                      e.stopPropagation();
                    }}
                    renderInput={(inputParams) => (
                      <TextField
                        {...inputParams}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onKeyDown={(e) => {
                          // Stop propagation to prevent grid navigation
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.7rem',
                            height: '32px',
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '0.7rem',
                            padding: '4px 8px',
                          },
                        }}
                      />
                    )}
                    PopperProps={{
                      sx: {
                        width: 'auto !important',
                        minWidth: '250px !important',
                        maxWidth: '400px !important', // ✅ dropdown can expand, grid won't
                      },
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            boundary: 'viewport',
                          },
                        },
                      ],
                    }}
                  />
                </Box>
              );
            } else {
              // Use TextField for other editable steps
              return (
                <Box
                  sx={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={editedRows[params.id]?.[col.field] || ''}
                    onChange={(event) =>
                      handleGridCellChange(params.id, col.field, event.target.value)
                    }
                    onKeyDown={(e) => {
                      // Stop propagation to prevent grid navigation
                      e.stopPropagation();
                      // Prevent default for arrow keys and Enter to avoid grid navigation
                      // But allow spacebar to work normally in the input
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
                        e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                        (e.key === 'Enter' && !e.shiftKey)) {
                        e.preventDefault();
                      }
                      // Spacebar should work normally - just stop propagation
                    }}
                    onKeyUp={(e) => {
                      // Stop propagation on keyup as well
                      e.stopPropagation();
                    }}
                    onKeyPress={(e) => {
                      // Stop propagation on keypress (for spacebar and other characters)
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      // Stop click propagation to prevent cell selection
                      e.stopPropagation();
                      // Focus the input when clicked
                      e.currentTarget.focus();
                    }}
                    onMouseDown={(e) => {
                      // Stop mousedown propagation to prevent cell selection
                      e.stopPropagation();
                    }}
                    onFocus={(e) => {
                      // Stop focus propagation to prevent grid from handling focus
                      e.stopPropagation();
                    }}
                    autoFocus={false}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.7rem',
                        height: '32px',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                      },
                    }}
                  />
                </Box>
              );
            }
          }
          // For compare step, add titleAccess (native tooltip) to all data cells
          if (isCurrentStepCompare()) {
            const cellValue = params.value || '';
            const displayValue = col.renderCell ? col.renderCell(params) : cellValue;

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%'
                }}
                title={cellValue ? String(cellValue) : 'No data'} // 🔹 Native hover tooltip
              >
                <Typography sx={{ fontSize: '0.7rem', width: '100%' }}>
                  {displayValue}
                </Typography>
              </Box>
            );
          }

          if (col.renderCell) {
            return col.renderCell(params);
          }
          return (
            <Typography sx={{ fontSize: '0.7rem' }}>{params.value}</Typography>
          );
        }
      };
    });

    // Add validation error column for COMPARE step
    if (isCurrentStepCompare()) {
      baseColumns.push({
        field: 'validation_errors',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const hasError = !isCurrentStepApproved && hasCompareStepValidationError(params.row.field, currentCompareRecordIndex);
          const errorMessage = getCompareFieldErrorMessage(params.row.field, currentCompareRecordIndex);

          if (hasError) {
            return (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}>
                <WarningIcon
                  sx={{ color: '#f44336', fontSize: '1.2rem' }}
                  titleAccess={errorMessage || 'Validation error'}
                />
              </Box>
            );
          }
          return null;
        }
      });
    }

    return baseColumns;
  };

  const parseFailedValidationKeysFromFundPayload = (fundPayload) => {
    const failedStepKeys = new Set();
    const rows = fundPayload?.data;

    if (!Array.isArray(rows)) return failedStepKeys;

    rows.forEach((row) => {
      const rawValidationErrors =
        row?.STEP_RESULTS ??
        row?.step_results ??
        row?.stepResults ??
        row?.VALIDATION_ERRORS ??
        row?.validation_errors ??
        row?.validationErrors ??
        null;
      if (!rawValidationErrors) return;
      try {
        const parsedErrors =
          typeof rawValidationErrors === 'string'
            ? JSON.parse(rawValidationErrors)
            : rawValidationErrors;
        if (!parsedErrors || typeof parsedErrors !== 'object') return;

        Object.entries(parsedErrors).forEach(([stepKey, value]) => {
          if (Number(value) > 0) {
            failedStepKeys.add(String(stepKey).trim().toUpperCase());
          }
        });
      } catch (e) {
        console.warn('Unable to parse STEP_RESULTS/VALIDATION_ERRORS payload:', rawValidationErrors);
      }
    });

    return failedStepKeys;
  };

  const parseApproveErrorsMapFromFileInstance = (fileInstance) => {
    const raw = fileInstance?.approveErrors ?? fileInstance?.approve_error ?? null;
    if (!raw) return null;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || typeof parsed !== 'object') return null;
      const normalized = {};
      Object.entries(parsed).forEach(([k, v]) => {
        normalized[String(k).trim().toUpperCase()] = v === true || v === 'true';
      });
      return normalized;
    } catch (e) {
      console.warn('Unable to parse approveErrors payload:', raw);
      return null;
    }
  };

  const getStepApprovalKey = (step) => {
    const key = step?.validationErrorStep || step?.stepName || '';
    return String(key).trim().toUpperCase();
  };

  function isStepApproved(step) {
    if (!step) return false;
    const key = getStepApprovalKey(step);
    if (!key) return false;
    return approveErrorsMap?.[key] === true;
  }

  /** True when all currently failed FastAPI validation keys are approved in approveErrors map. */
  function areAllFailedValidationKeysApproved(fundPayload, approveMap) {
    if (!approveMap || typeof approveMap !== 'object') return false;
    const failedKeys = [...parseFailedValidationKeysFromFundPayload(fundPayload)];
    if (failedKeys.length === 0) return false;
    return failedKeys.every((k) => approveMap[k] === true);
  }

  const getPreferredInitialStepIndexFromPayloads = (sortedSteps, fundPayload, approveMap) => {
    if (!Array.isArray(sortedSteps) || sortedSteps.length === 0) return -1;

    const failedKeys = parseFailedValidationKeysFromFundPayload(fundPayload);
    const approve = approveMap && typeof approveMap === 'object' ? approveMap : null;

    // Every workflow step key has approveErrors=true → last step (ignore FastAPI).
    if (approve && Object.keys(approve).length > 0) {
      const allApproved = sortedSteps.every((s) => {
        const k = getStepApprovalKey(s);
        return !k ? true : approve[k] === true;
      });
      if (allApproved) {
        return sortedSteps.length - 1;
      }
    }

    // No validation errors from FastAPI → last step (do not halt on missing approve entries).
    if (failedKeys.size === 0) {
      return sortedSteps.length - 1;
    }

    // FastAPI reported failures, but each failing key is approved → last step.
    const allFailureKeysApproved = [...failedKeys].every((k) => approve?.[k] === true);
    if (allFailureKeysApproved) {
      return sortedSteps.length - 1;
    }

    // Halt at first visible step whose key is in failedKeys and not approved.
    const blockingIndex = sortedSteps.findIndex((s) => {
      const k = getStepApprovalKey(s);
      if (!k) return false;
      if (approve?.[k] === true) return false;
      return failedKeys.has(k);
    });

    if (blockingIndex !== -1) return blockingIndex;

    return sortedSteps.length - 1;
  };

  const getBlockingValidationStepIndexFromPayloads = (sortedSteps, fundPayload, approveMap) => {
    if (!Array.isArray(sortedSteps) || sortedSteps.length === 0) return -1;
    const failedKeys = parseFailedValidationKeysFromFundPayload(fundPayload);
    if (failedKeys.size === 0) return -1;

    // If all failed keys are already approved, nothing to block.
    const allFailureKeysApproved = [...failedKeys].every((k) => approveMap?.[k] === true);
    if (allFailureKeysApproved) return -1;

    return sortedSteps.findIndex((s) => {
      const k = getStepApprovalKey(s);
      if (!k) return false;
      if (approveMap?.[k] === true) return false;
      return failedKeys.has(k);
    });
  };

  const getPreferredInitialStepIndexFromFundPayload = (sortedSteps, fundPayload) => {
    if (!Array.isArray(sortedSteps) || sortedSteps.length === 0) return -1;

    const failedStepKeys = parseFailedValidationKeysFromFundPayload(fundPayload);
    if (failedStepKeys.size > 0) {
      const failedStepIndex = sortedSteps.findIndex(
        step => step?.validationErrorStep && failedStepKeys.has(String(step.validationErrorStep).trim().toUpperCase())
      );
      if (failedStepIndex !== -1) return failedStepIndex;
    }

    return sortedSteps.length - 1;
  };

  const getPreferredInitialStepIndex = (sortedSteps) =>
    getPreferredInitialStepIndexFromPayloads(sortedSteps, fundAdministrationValidations, approveErrorsMap);

  const fetchFundAdministrationValidationsPayload = async (workflowDefinition) => {
    if (!workflowInstanceId) return null;
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const params = new URLSearchParams({ process_id: String(workflowInstanceId) });
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        params.set('tenant_id', tenantId);
      }
      const validationView = workflowDefinition?.validationView || workflowDefinition?.validation_view;
      const snowflakeTableName =
        workflowDefinition?.snowflakeTableName || workflowDefinition?.snowflake_table_name;
      if (validationView) {
        params.set('validation_view', String(validationView).trim());
      }
      if (snowflakeTableName) {
        params.set('snowflake_table_name', String(snowflakeTableName).trim());
      }
      if (!validationView && !snowflakeTableName) {
        return null;
      }
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/stepdata/fund-administration-validations?${params.toString()}`;
      const resp = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) return null;
      return await resp.json();
    } catch (e) {
      return null;
    }
  };

  const fetchFileInstanceDetailsPayload = async () => {
    if (!fileInstanceId) return { fileInstance: null, approveMap: null };
    try {
      const fileInstance = await fetchFileInstance(fileInstanceId);
      setFileInstanceDetails(fileInstance);
      const map = parseApproveErrorsMapFromFileInstance(fileInstance);
      setApproveErrorsMap(map);
      return { fileInstance, approveMap: map };
    } catch (e) {
      setFileInstanceDetails(null);
      setApproveErrorsMap(null);
      return { fileInstance: null, approveMap: null };
    }
  };

  const maybeCallErrorIdentifierClear = async (approveMap) => {
    if (!fileInstanceId || !approveMap) return;
    if (!areAllFailedValidationKeysApproved(fundAdministrationValidations, approveMap)) return;
    if (errorIdentifierClearCalledRef.current) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/error-identifier`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ identifier: 0 }),
        }
      );

      if (!resp.ok) {
        console.warn('error-identifier call failed:', resp.status, await resp.text());
        return;
      }

      errorIdentifierClearCalledRef.current = true;
      await fetchFileInstanceDetailsPayload();
      showInfo('Validation error identifier updated', 'File instance');
    } catch (e) {
      console.warn('error-identifier call error:', e);
    }
  };

  useEffect(() => {
    errorIdentifierClearCalledRef.current = false;
  }, [fileInstanceId]);

  useEffect(() => {
    if (!approveErrorsMap || !allFetchedSteps.length) return;
    maybeCallErrorIdentifierClear(approveErrorsMap);
  }, [approveErrorsMap, allFetchedSteps, fundAdministrationValidations]);

  useEffect(() => {
    if (fileInstanceId) {
      fetchPdfUrl(fileInstanceId);
      fetchApprovalStatus();
    }
  }, [fileInstanceId]);

  useEffect(() => {
    if (
      !fileInstanceId ||
      !workflowDefinitionSnowflakeId ||
      workflowInstanceId == null ||
      workflowInstanceId === ''
    ) {
      return;
    }

    bootstrapAbortRef.current?.abort();
    const abortController = new AbortController();
    bootstrapAbortRef.current = abortController;
    initialBootstrapDoneRef.current = false;
    stepLoadCompletedKeyRef.current = null;

    const fetchWorkflowStepsAndFirstStep = async () => {
      try {
        setStepperLoading(true);
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        if (!token || !tenantId) throw new Error('No authentication token or tenant ID found');

        // Load file instance details early (includes approveErrors gating)
        const { approveMap: approveMapForInitialStep } = await fetchFileInstanceDetailsPayload();
        if (abortController.signal.aborted) return;

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/tenant/${tenantId}?tenantWorkflowId=${workflowDefinitionSnowflakeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (abortController.signal.aborted) return;

        // Fetch condition fields data with view_name parameter
        const { conditionData, workflowDefinition } = await fetchConditionFieldsWithViewName();
        if (abortController.signal.aborted) return;

        let sortedSteps = data.sort((a, b) => a.stepOrder - b.stepOrder);

        // **STEP 1: Filter by visibility first - show all steps where visible = true**
        const harmonateUser = isServerHarmonateUser();
        if (!harmonateUser) {
          // First filter: Only show steps that are visible to customers
          sortedSteps = sortedSteps.filter(step => step.visibleToCustomer === true);
        }

        // Fetch workflow-specific STEP_VALIDATION_VIEW so preferred step uses fresh payload.
        const fundPayload = await fetchFundAdministrationValidationsPayload(workflowDefinition);
        if (abortController.signal.aborted) return;

        const preferredStepIndex =
          sortedSteps.length > 0
            ? getPreferredInitialStepIndexFromPayloads(sortedSteps, fundPayload, approveMapForInitialStep)
            : -1;

        // Set steps + fund together so effects that depend on both do not see last-step fallback first.
        setAllFetchedSteps(data);
        setFundAdministrationValidations(fundPayload);
        setSteps(sortedSteps);
        setStepperError(null);

        if (sortedSteps.length > 0) {
          const visiblePreferred = resolveToRunConditionVisibleStepIndex(preferredStepIndex, sortedSteps, conditionData);
          setActiveStep(visiblePreferred);
          await loadStepData(visiblePreferred, sortedSteps, conditionData);
          if (abortController.signal.aborted) return;
          scrollStepperToStep(visiblePreferred, sortedSteps, conditionData);
        }

        initialBootstrapDoneRef.current = true;
      } catch (err) {
        if (abortController.signal.aborted) return;
        console.error('Error fetching workflow steps:', err);
        setStepperError(err.message);
        setSteps([]);
        setAllFetchedSteps([]);
        setFundAdministrationValidations(null);
      } finally {
        if (!abortController.signal.aborted) {
          setStepperLoading(false);
        }
      }
    };
    fetchWorkflowStepsAndFirstStep();

    return () => {
      abortController.abort();
      initialBootstrapDoneRef.current = false;
      stepLoadCompletedKeyRef.current = null;
    };
  }, [workflowDefinitionSnowflakeId, fileInstanceId, workflowInstanceId]);

  useEffect(() => {
    handleStepperScroll();
  }, [steps, stepperLoading]);

  // Sync stepper selection when validations / run conditions change (no step data re-fetch).
  useEffect(() => {
    if (!initialBootstrapDoneRef.current) return;
    if (allFetchedSteps.length === 0) return;

    let sortedSteps = allFetchedSteps.slice().sort((a, b) => a.stepOrder - b.stepOrder);

    const harmonateUser = isServerHarmonateUser();
    if (!harmonateUser) {
      sortedSteps = sortedSteps.filter(step => step.visibleToCustomer === true);
    }

    setSteps(sortedSteps);
    if (sortedSteps.length === 0) return;

    const preferredStepIndex = getPreferredInitialStepIndex(sortedSteps);
    const preferredVisible = resolveToRunConditionVisibleStepIndex(
      preferredStepIndex,
      sortedSteps,
      conditionFieldsData
    );
    const shouldJumpToFailedStep = preferredVisible !== -1 && activeStep !== preferredVisible;

    let targetStep = activeStep;

    if (activeStep === -1 || activeStep >= sortedSteps.length || shouldJumpToFailedStep) {
      targetStep = preferredVisible;
    } else {
      const activeVisible = resolveToRunConditionVisibleStepIndex(
        activeStep,
        sortedSteps,
        conditionFieldsData
      );
      if (activeVisible !== activeStep) {
        targetStep = activeVisible;
      }
    }

    if (targetStep !== -1 && targetStep !== activeStep) {
      setActiveStep(targetStep);
    }

    const scrollIndex = targetStep !== -1 ? targetStep : activeStep;
    if (scrollIndex !== -1) {
      scrollStepperToStep(scrollIndex, sortedSteps, conditionFieldsData);
    }
  }, [allFetchedSteps, conditionFieldsData, fundAdministrationValidations, skipRunConditionCheck]);

  const buildStepLoadKey = (stepIndex, stepsArray) => {
    const step = stepsArray?.[stepIndex];
    return [
      step?.workflowStepId ?? '',
      fastApiFileInstanceId ?? '',
      workflowInstanceId ?? '',
      stepIndex,
    ].join('|');
  };

  const loadStepData = async (
    stepIndex,
    stepsArray = steps,
    conditionData = conditionFieldsData,
    options = {}
  ) => {
    const { force = false } = options;
    const cacheKey = buildStepLoadKey(stepIndex, stepsArray);

    if (!force) {
      if (stepLoadInflightRef.current?.key === cacheKey) {
        return stepLoadInflightRef.current.promise;
      }
      if (stepLoadCompletedKeyRef.current === cacheKey) {
        return;
      }
    } else {
      stepLoadCompletedKeyRef.current = null;
    }

    const requestId = ++loadStepRequestRef.current;

    const runLoad = async () => {
    try {
      setStepDataLoading(true);
      setStepDataError(null);
      setComparisonError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const selectedStep = stepsArray[stepIndex];
      if (!selectedStep || !selectedStep.workflowStepId) throw new Error('Invalid step selected');
      const stepId = selectedStep.workflowStepId;
      const stepDetailsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/${stepId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!stepDetailsResponse.ok) throw new Error(`Failed to fetch step details: ${stepDetailsResponse.status}`);
      const stepDetailsData = await stepDetailsResponse.json();
      setCurrentStepDetails(stepDetailsData); // Store step details including payload
      const additionalInstructions = stepDetailsData.additionalInstructions;
      if (!additionalInstructions) throw new Error('No additional instructions found for this step');
      let gridParams;
      try {
        const instructionsData = JSON.parse(additionalInstructions);
        gridParams = instructionsData.grid;
        if (!gridParams) throw new Error('No grid data found in additional instructions');
      } catch (parseError) {
        throw new Error('Failed to parse additional instructions JSON');
      }
      let fetchedColumns = [];
      let fetchedValidationErrors = [];
      let compareConfigData = null;
      let viewApiData = null;
      let dropdownField = null;
      if (gridParams && gridParams.view_name) {
        const viewApiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${encodeURIComponent(gridParams.view_name)}`;
        const viewApiResponse = await fetch(viewApiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (viewApiResponse.ok) {
          // const viewApiData = await viewApiResponse.json();
          viewApiData = await viewApiResponse.json();
          if (viewApiData.grid && Array.isArray(viewApiData.grid.columns)) {
            fetchedColumns = viewApiData.grid.columns;
          }
          if (viewApiData.grid && viewApiData.grid.errors && Array.isArray(viewApiData.grid.errors)) {
            fetchedValidationErrors = viewApiData.grid.errors;
            console.log('Validation errors fetched from grid.errors:', fetchedValidationErrors);
          }
          // Store compare config data if it exists
          if (viewApiData.compare) {
            compareConfigData = viewApiData;
            setCompareConfigData(compareConfigData);
          }

          // Check for view_grid and print flags
          if (viewApiData.view_grid === true) {
            setViewGridEnabled(true);
          } else {
            // If step has editable fields, enable grid view automatically
            const hasEditableFields = fetchedColumns.some(col => col.editable === true);
            if (hasEditableFields) {
              setViewGridEnabled(true);
            } else {
              setViewGridEnabled(false);
            }
          }

          // Handle both boolean true and string "excel" values
          const isSummaryApproval = SUMMARY_APPROVAL_STEP_NAMES.includes(selectedStep.stepName);
          if (viewApiData.print === true || viewApiData.print === 'excel' || isSummaryApproval) {
            setPrintEnabled(true);
            // Set print type based on the value
            if (viewApiData.print === 'excel') {
              setPrintType('excel');
            } else {
              setPrintType('pdf');
            }
          } else {
            setPrintEnabled(false);
            setPrintType('pdf');
          }

          // Handle approve_errors flag
          if (viewApiData.approve_errors === true || viewApiData.approve_errors === 'true') {
            setApproveErrorsEnabled(true);
          } else {
            setApproveErrorsEnabled(false);
          }

          // Extract dropdown field name from API response
          if (viewApiData.dropdown) {
            dropdownField = viewApiData.dropdown;
            setDropdownFieldName(viewApiData.dropdown);
            console.log('Dropdown field from API:', viewApiData.dropdown);
          } else {
            dropdownField = null;
            setDropdownFieldName(null);
          }
        }
      }

      const queryParams = new URLSearchParams();
      // if (gridParams.view_name) queryParams.append('view_name', gridParams.view_name);
      if (viewApiData && viewApiData.category) {
        queryParams.append('view_name', viewApiData.category);
      }
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) queryParams.set('tenant_id', tenantId);
      if (fastApiFileInstanceId) queryParams.set('file_instance_id', fastApiFileInstanceId);
      if (workflowInstanceId) queryParams.set('workflow_instance_id', workflowInstanceId);
      const workflowStepResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/step?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!workflowStepResponse.ok) throw new Error(`Failed to fetch workflow step data: ${workflowStepResponse.status}`);
      const workflowStepData = await workflowStepResponse.json();
      if (requestId !== loadStepRequestRef.current) return;

      // Check if this is a COMPARE step
      const currentStep = stepsArray[stepIndex];
      const isCompareStep = currentStep && currentStep.stepType === 'COMPARE';

      if (workflowStepData && workflowStepData.data) {
        const preparedData = processData(prepareDataForGrid(workflowStepData.data), fetchedColumns);

        // Populate dropdown with unique values from the dropdown field
        if (dropdownField && preparedData.length > 0) {
          const uniqueValues = new Set();
          const firstRecord = preparedData[0];

          // Try to find the field with different variations
          let actualFieldName = null;
          const variations = [
            dropdownField,
            dropdownField.toUpperCase(),
            dropdownField.toLowerCase(),
            dropdownField.replace(/\s+/g, '_'),
            dropdownField.replace(/\s+/g, '_').toUpperCase(),
            dropdownField.replace(/\s+/g, '_').toLowerCase(),
          ];

          // Find which variation exists in the data
          for (const variation of variations) {
            if (firstRecord.hasOwnProperty(variation)) {
              actualFieldName = variation;
              break;
            }
          }

          if (actualFieldName) {
            preparedData.forEach(record => {
              const value = record[actualFieldName];
              if (value !== null && value !== undefined && value !== '') {
                // Preserve original type (string or number)
                uniqueValues.add(value);
              }
            });

            // Convert to array and sort
            const sortedValues = Array.from(uniqueValues).sort((a, b) => {
              // Sort numbers numerically, strings alphabetically
              if (typeof a === 'number' && typeof b === 'number') {
                return a - b;
              }
              return String(a).localeCompare(String(b));
            });

            setRowsList(sortedValues);
            setSelectedRow(null); // Reset selection when data changes
            console.log('Populated dropdown with values:', sortedValues, 'from field:', actualFieldName, '(original:', dropdownField, ')');
          } else {
            console.warn('Dropdown field not found in data:', dropdownField, 'Available fields:', Object.keys(firstRecord));
            setRowsList([]);
            setSelectedRow(null);
          }
        } else {
          setRowsList([]);
          setSelectedRow(null);
        }

        if (isCompareStep && compareConfigData && compareConfigData.compare) {
          // For COMPARE steps, fetch compare view data and create compare grid
          const compareViewName = compareConfigData.compare.compare_view;
          if (compareViewName) {
            // Store the main step data for compare navigation
            setMainStepDataForCompare(preparedData);

            // Fetch compare view data first
            const currentCompareViewData = await fetchCompareViewData(compareViewName);

            // Create compare grid data structure (start with index 0)
            const compareGridData = createCompareGridData(compareConfigData, preparedData, currentCompareViewData, 0);
            console.log('Compare grid data created:', compareGridData);
            console.log('Compare config:', compareConfigData);
            console.log('Main step data:', preparedData);
            console.log('Compare view data:', currentCompareViewData);
            setComparisonData(compareGridData);

            // Set columns for compare grid with validation indicator
            const compareColumns = fetchedColumns.filter(col => col.visible !== false).map(col => ({
              field: col.name,
              headerName: col.title || col.name,
              width: col.width ? parseInt(col.width) : 150,
              flex: col.width ? undefined : 1,
              filterable: true,
              sortable: true,
              valueGetter: (params) => params.row[col.name],
            }));
            setDynamicColumns(compareColumns);
          } else {
            // Fallback to regular grid if no compare view
            const columns = fetchedColumns.filter(col => col.visible !== false).map(col => ({
              field: col.name,
              headerName: col.title || col.name,
              width: col.width ? parseInt(col.width) : 150,
              flex: col.width ? undefined : 1,
              filterable: true,
              sortable: true,
              valueGetter: (params) => params.row[col.name],
            }));
            setComparisonData(preparedData);
            setDynamicColumns(columns);
          }
        } else {
          // Regular step handling
          const columns = fetchedColumns.filter(col => col.visible !== false).map(col => ({
            field: col.name,
            headerName: col.title || col.name,
            width: col.width ? parseInt(col.width) : 150,
            flex: col.width ? undefined : 1,
            filterable: true,
            sortable: true,
            valueGetter: (params) => params.row[col.name],
          }));
          setComparisonData(preparedData);
          setDynamicColumns(columns);
        }

        setViewColumns(fetchedColumns);
        setViewValidationErrors(fetchedValidationErrors);
      } else {
        setViewColumns(fetchedColumns);
        setViewValidationErrors(fetchedValidationErrors);
        setComparisonData([]);
        setDynamicColumns([]);
      }
      setActiveStep(stepIndex);
      console.log('Step data loaded successfully:', workflowStepData);
    } catch (error) {
      if (requestId !== loadStepRequestRef.current) return;
      console.error('Error loading step data:', error);
      setStepDataError(error.message);
      setComparisonData([]);
      setDynamicColumns([]);
    } finally {
      if (requestId === loadStepRequestRef.current) {
        setStepDataLoading(false);
      }
    }
    };

    const promise = runLoad();
    stepLoadInflightRef.current = { key: cacheKey, promise };
    try {
      await promise;
      if (requestId === loadStepRequestRef.current) {
        stepLoadCompletedKeyRef.current = cacheKey;
      }
    } finally {
      if (stepLoadInflightRef.current?.key === cacheKey) {
        stepLoadInflightRef.current = null;
      }
    }
  };


  const handleStepClick = async (stepIndex) => {
    const blockingStepIndex = getBlockingValidationStepIndexFromPayloads(steps, fundAdministrationValidations, approveErrorsMap);
    if (blockingStepIndex !== -1 && stepIndex > blockingStepIndex) {
      const blockingStepName = steps?.[blockingStepIndex]?.stepName || `Step ${blockingStepIndex + 1}`;
      showInfo(`Resolve or approve validation errors in "${blockingStepName}" before moving to next steps`, 'Step blocked');
      return;
    }
    stepLoadCompletedKeyRef.current = null;
    await loadStepData(stepIndex, steps, conditionFieldsData, { force: true });
  };

  const handleIncrement = () => setInvestorNumber((prev) => prev + 1);
  const handleDecrement = () => setInvestorNumber((prev) => (prev > 1 ? prev - 1 : 1));

  // Function to export data as Excel
  const handleExcelExport = () => {
    // Get all data rows to export - use filtered data
    const dataToExport = filteredComparisonData && filteredComparisonData.length > 0 ? filteredComparisonData : (isCurrentStepTransform() ? [getCurrentRecordData()] : []);


    if (!dataToExport || dataToExport.length === 0 || !dynamicColumns) {
      console.error('No data available for Excel export');
      return;
    }

    // Prepare data for Excel export
    const excelData = [];

    // Add header row with all column headers
    const headers = dynamicColumns
      .filter(col => !col.field.startsWith('_') && col.field !== 'id')
      .map(col => col.headerName || formatHeaderName(col.field));
    excelData.push(headers);

    // Add data rows for each record
    dataToExport.forEach((record, index) => {
      const rowData = [];

      dynamicColumns.forEach((col) => {
        // Skip internal fields that start with underscore or are IDs we don't want to show
        if (!col.field.startsWith('_') && col.field !== 'id') {
          const value = record[col.field];

          // Format the value using the column's renderCell if available
          let formattedValue;
          if (col.renderCell && value != null) {
            try {
              // Try to get the formatted value from renderCell
              const cellParams = {
                row: record,
                value: value,
                id: record?.id,
                field: col.field,
              };
              const rendered = col.renderCell(cellParams);

              // If renderCell returns a React component, extract the text content
              if (typeof rendered === 'object' && rendered.props && rendered.props.children) {
                formattedValue = String(rendered.props.children);
              } else {
                formattedValue = String(rendered);
              }
            } catch (e) {
              // Fallback to raw value if renderCell fails. Empty cells stay blank so GL ingest accepts the file.
              formattedValue = value != null ? String(value) : '';
            }
          } else {
            formattedValue = value != null ? String(value) : '';
          }

          if (formattedValue === 'Not Applicable') {
            formattedValue = '';
          }

          rowData.push(formattedValue);
        }
      });

      excelData.push(rowData);
    });

    // Extract fund name and reporting date from first record (for metadata)
    const firstRecord = dataToExport[0];
    const fundName = firstRecord?.FUND ||
      firstRecord?.REFERENCE_FUND_NAME ||
      firstRecord?.PROVIDED_FUND_NAME ||
      firstRecord?.FUND_NAME ||
      'Unknown Fund';

    const reportingDate = firstRecord?.DATE ||
      firstRecord?.REPORTING_DATE ||
      firstRecord?.PERIOD_DATE ||
      firstRecord?.END_DATE ||
      firstRecord?.PERIOD_END_DATE ||
      'Unknown Period';

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();

    // Add metadata sheet
    const currentStep = steps[activeStep]?.stepName || 'Summary';
    const metadata = [
      [currentStep],
      [''],
      [`Quality Check for ${fundName}'s Investor Statements for Period ${reportingDate}`],
      [''],
      [`Generated on: ${formatDateTime(new Date())}`],
      ['']
    ];

    const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);

    // Add main data sheet first. GL ingest only accepts the file when Summary Data is the first tab.
    const dataSheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths - dynamic based on number of columns
    const colWidths = headers.map(() => ({ width: 25 }));
    dataSheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Summary Data');
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Info');

    // Save the Excel file with a formatted timestamp
    const timestamp = formatDateTime(new Date(), 'YYYY-MM-DD-HH-mm-ss');
    XLSX.writeFile(workbook, `summary-report-${timestamp}.xlsx`);
  };

  const handlePrint = async () => {
    // Log the print/export action
    console.log('Attempting to log print/export action...', { fileInstanceId, fileName, printType });
    try {
      console.log('Calling AuditLoggers.documentPrintedOrExported...');
      const result = await AuditLoggers.documentPrintedOrExported(
        fileInstanceId,
        fileName,
        printType === 'excel' ? 'excel' : 'pdf'
      );
      console.log('Audit log result:', result);
    } catch (error) {
      console.error('Error logging print/export action:', error);
      // Don't fail the print/export if logging fails
    }

    // Check print type and call appropriate function
    if (printType === 'excel') {
      handleExcelExport();
      return;
    }

    // Original PDF export logic
    // Create a new PDF document
    const doc = new jsPDF();

    // Get the current data to display (PDF should remain as single record Field-Value format)
    const currentData = isCurrentStepTransform() ? getCurrentRecordData() : filteredComparisonData[0];

    // Define columns for the PDF table - using the actual summary data structure
    const columns = [
      { header: 'Field', dataKey: 'field' },
      { header: 'Value', dataKey: 'value' }
    ];

    // Prepare data for the PDF table using dynamicColumns for proper formatting
    const data = [];

    // Use dynamicColumns to get the proper field names and formatting
    if (currentData && dynamicColumns) {
      dynamicColumns.forEach((col) => {
        const value = currentData[col.field];

        // Skip internal fields that start with underscore or are IDs we don't want to show
        if (!col.field.startsWith('_') && col.field !== 'id') {
          // Use the headerName from column definition for consistent formatting
          const formattedKey = col.headerName || formatHeaderName(col.field);

          // Format the value using the column's renderCell if available
          let formattedValue;
          if (col.renderCell && value != null) {
            try {
              // Try to get the formatted value from renderCell
              const cellParams = {
                row: currentData,
                value: value,
                id: currentData?.id,
                field: col.field,
              };
              const rendered = col.renderCell(cellParams);

              // If renderCell returns a React component, extract the text content
              if (typeof rendered === 'object' && rendered.props && rendered.props.children) {
                formattedValue = String(rendered.props.children);
              } else {
                formattedValue = String(rendered);
              }
            } catch (e) {
              // Fallback to raw value if renderCell fails
              formattedValue = value != null ? String(value) : 'Not Applicable';
            }
          } else {
            formattedValue = value != null ? String(value) : 'Not Applicable';
          }

          data.push({
            field: formattedKey,
            value: formattedValue
          });
        }
      });
    }

    // Extract fund name and reporting date from currentData
    const fundName = currentData?.FUND ||
      currentData?.REFERENCE_FUND_NAME ||
      currentData?.PROVIDED_FUND_NAME ||
      currentData?.FUND_NAME ||
      'Unknown Fund';

    const reportingDate = currentData?.DATE ||
      currentData?.REPORTING_DATE ||
      currentData?.PERIOD_DATE ||
      currentData?.END_DATE ||
      currentData?.PERIOD_END_DATE ||
      'Unknown Period';

    // Add title and date
    doc.setFontSize(18);
    doc.text('Summary Report', 14, 22);

    // Add quality check header
    doc.setFontSize(12);
    doc.setTextColor(60);
    doc.text(`Quality Check for ${fundName}'s Investor Statements for Period ${reportingDate}`, 14, 32);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 14, 42);

    // Add the table to the PDF
    doc.autoTable({
      head: [columns.map(col => col.header)],
      body: data.map(row => [row.field, row.value]),
      startY: 50,
      headStyles: {
        fillColor: [76, 175, 80],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'left',
        textColor: [255, 255, 255],
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'bold' },
        1: { cellWidth: 'auto' }
      },
      margin: { top: 40 },
      styles: {
        overflow: 'linebreak',
        lineWidth: 0.1,
        lineColor: 200
      }
    });

    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 30,
        doc.internal.pageSize.getHeight() - 10
      );
    }

    // Save the PDF with a formatted timestamp
    const timestamp = formatDateTime(new Date(), 'YYYY-MM-DD-HH-mm-ss');
    doc.save(`summary-report-${timestamp}.pdf`);
  };

  const handleApproveErrors = async () => {
    try {
      console.log('Approving errors for step:', steps[activeStep]?.stepName);
      
      // Log the approve errors action
      try {
        const result = await AuditLoggers.documentPrintedOrExported(
          fileInstanceId,
          fileName,
          'approve_errors'
        );
        console.log('Audit log result for approve errors:', result);
      } catch (error) {
        console.error('Error logging approve errors action:', error);
        // Don't fail the action if logging fails
      }

      // Use validationErrorStep key if available (matches FastAPI VALIDATION_ERRORS keys)
      const currentStep = steps[activeStep];
      const currentStepKey = currentStep?.validationErrorStep || currentStep?.stepName;
      if (!currentStepKey) {
        showError('Current step not identified', 'Approve Errors Error');
        return;
      }

      // Merge with existing approveErrors so we don't overwrite previous approvals
      const existingMap = parseApproveErrorsMapFromFileInstance(fileInstanceDetails) || {};
      const merged = { ...existingMap, [String(currentStepKey).trim().toUpperCase()]: true };

      // Call the PATCH API to approve errors
      const token = localStorage.getItem('token');
      if (!token) {
        showError('No authentication token found', 'Approve Errors Error');
        return;
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/approve-errors`;
      
      console.log('Calling approve errors API:', apiUrl);
      console.log('Request body:', { approveErrors: JSON.stringify(merged) });

      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          approveErrors: JSON.stringify(merged)
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to approve errors: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Approve errors response:', result);

      showSuccess('Errors approved successfully', 'Approve Errors');
      // Refresh file instance details (approveErrors) after approving
      await fetchFileInstanceDetailsPayload();
      
      // Refresh the data after approving errors
      if (activeStep >= 0 && steps.length > 0) {
        await loadStepData(activeStep, steps, conditionFieldsData, { force: true });
      }
    } catch (error) {
      console.error('Error approving errors:', error);
      showError(`Failed to approve errors: ${error.message}`, 'Approve Errors Error');
    }
  };

  const handleRetryPdf = () => {
    if (fileInstanceId) fetchPdfUrl(fileInstanceId);
  };

  const renderJsonData = (data) => {
    return (
      <pre style={{
        backgroundColor: '#f5f5f5',
        padding: '16px',
        borderRadius: '4px',
        overflow: 'auto',
        maxHeight: '400px'
      }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton sx={{ fontSize: '0.7rem' }} />
      <GridToolbarFilterButton sx={{ fontSize: '0.7rem' }} />
    </GridToolbarContainer>
  );

  const getValidationFields = (row) => {
    const validationFields = Object.keys(row).filter(
      (key) => key.endsWith('_VALIDATION') && typeof row[key] !== 'undefined'
    );

    // Add blank field validation fields ONLY for specific steps: GP, Fund, Investors
    const currentStep = steps[activeStep];
    if (currentStep && ['GP', 'Fund', 'Investors'].includes(currentStep.stepName)) {
      const blankFieldValidations = getBlankFieldErrors(row);
      validationFields.push(...blankFieldValidations);
    }

    return validationFields;
  };

  // NEW: Check for blank required fields based on current step
  const getBlankFieldErrors = (row) => {
    const currentStep = steps[activeStep];
    if (!currentStep) return [];

    const blankErrors = [];

    // Check specific fields based on current step
    if (currentStep.stepName === 'Fund' && (!row.REFERENCE_FUND_NAME || row.REFERENCE_FUND_NAME.trim() === '')) {
      blankErrors.push('REFERENCE_FUND_NAME_VALIDATION');
    }

    if (currentStep.stepName === 'GP' && (!row.INVESTOR || row.INVESTOR.trim() === '')) {
      blankErrors.push('INVESTOR_VALIDATION');
    }

    if (currentStep.stepName === 'Investors' && (!row.REFERENCE_INVESTOR || row.REFERENCE_INVESTOR.trim() === '')) {
      blankErrors.push('REFERENCE_INVESTOR_VALIDATION');
    }

    return blankErrors;
  };

  const rowHasValidationError = (row) => {
    return getValidationFields(row).some((key) => {
      // For actual _VALIDATION fields from the data
      if (row[key] !== undefined) {
        const val = row[key];
        return val === false || val === 'false' || val === 0 || val === '0';
      }
      // For blank field validations (these are always considered errors)
      return true;
    });
  };

  const getValidationErrorMessages = (row) => {
    if (!viewValidationErrors || viewValidationErrors.length === 0) {
      return getValidationFields(row)
        .filter((key) => {
          // For actual _VALIDATION fields from the data
          if (row[key] !== undefined) {
            const val = row[key];
            return val === false || val === 'false' || val === 0 || val === '0';
          }
          // For blank field validations (these are always considered errors)
          return true;
        })
        .map((key) => `Validation failed for ${formatHeaderName(key.replace(/_VALIDATION$/, ''))}`);
    }
    return getValidationFields(row)
      .filter((key) => {
        // For actual _VALIDATION fields from the data
        if (row[key] !== undefined) {
          const val = row[key];
          return val === false || val === 'false' || val === 0 || val === '0';
        }
        // For blank field validations (these are always considered errors)
        return true;
      })
      .map((key) => {
        const err = viewValidationErrors.find(e => e.validation_field_name === key);
        return err ? err.validation_message : `Validation failed for ${formatHeaderName(key.replace(/_VALIDATION$/, ''))}`;
      });
  };

  // NEW: COMPARE step validation logic
  const getCompareStepValidationErrors = (mainStepData, currentRecordIndex = 0) => {
    if (!isCurrentStepCompare() || !mainStepData || mainStepData.length === 0) {
      return [];
    }

    const currentRecord = mainStepData[currentRecordIndex] || mainStepData[0];
    if (!currentRecord) return [];

    const validationErrors = [];
    const validationFields = getValidationFields(currentRecord);

    validationFields.forEach(validationField => {
      // For actual _VALIDATION fields from the data
      if (currentRecord[validationField] !== undefined) {
        const val = currentRecord[validationField];
        if (val === false || val === 'false' || val === 0 || val === '0') {
          // Map validation field to the corresponding field name for COMPARE grid
          const fieldName = validationField.replace(/_VALIDATION$/, '');

          // Find custom error message
          const err = viewValidationErrors?.find(e => e.validation_field_name === validationField);
          const errorMessage = err ? err.validation_message : `Validation failed for ${formatHeaderName(fieldName)}`;

          validationErrors.push({
            fieldName: fieldName,
            validationField: validationField,
            errorMessage: errorMessage,
            recordIndex: currentRecordIndex,
            investorName: currentRecord.INVESTOR || currentRecord.investor || currentRecord.PROVIDED_INVESTOR || currentRecord.PROVIDED_FUND_NAME || currentRecord.FUND_NAME || currentRecord.POSITION || `Record ${currentRecordIndex + 1}`
          });
        }
      } else {
        // For blank field validations (these are always considered errors)
        const fieldName = validationField.replace(/_VALIDATION$/, '');

        // Find custom error message from API
        const err = viewValidationErrors?.find(e => e.validation_field_name === validationField);
        const errorMessage = err ? err.validation_message : `Validation failed for ${formatHeaderName(fieldName)}`;

        validationErrors.push({
          fieldName: fieldName,
          validationField: validationField,
          errorMessage: errorMessage,
          recordIndex: currentRecordIndex,
          investorName: currentRecord.INVESTOR || currentRecord.investor || currentRecord.PROVIDED_INVESTOR || currentRecord.PROVIDED_FUND_NAME || currentRecord.FUND_NAME || currentRecord.POSITION || `Record ${currentRecordIndex + 1}`
        });
      }
    });

    return validationErrors;
  };

  const hasCompareStepValidationError = (fieldName, currentRecordIndex = 0) => {
    if (!isCurrentStepCompare() || !mainStepDataForCompare || mainStepDataForCompare.length === 0) {
      return false;
    }

    const currentRecord = mainStepDataForCompare[currentRecordIndex] || mainStepDataForCompare[0];
    if (!currentRecord) return false;

    // Convert display field name to snake_case validation field name
    // "Committed Capital" -> "COMMITTED_CAPITAL_VALIDATION"
    const validationFieldName = fieldName.toUpperCase().replace(/\s+/g, '_') + '_VALIDATION';

    // Check if this field has a validation error by checking the actual value
    if (currentRecord[validationFieldName] !== undefined) {
      const val = currentRecord[validationFieldName];
      const hasError = val === false || val === 'false' || val === 0 || val === '0';

      console.log('Checking validation for field:', fieldName);
      console.log('Validation field name:', validationFieldName);
      console.log('Validation value:', val);
      console.log('Has validation error:', hasError);

      return hasError;
    }

    // If the validation field doesn't exist, check if it's a blank field validation
    // But only for the specific steps that support blank field validation
    const currentStep = steps[activeStep];
    if (currentStep && ['GP', 'Fund', 'Investors'].includes(currentStep.stepName)) {
      const blankFieldValidations = getBlankFieldErrors(currentRecord);
      const hasBlankError = blankFieldValidations.includes(validationFieldName);

      console.log('Checking blank field validation for field:', fieldName);
      console.log('Has blank field error:', hasBlankError);

      return hasBlankError;
    }

    return false;
  };

  const getCompareFieldErrorMessage = (fieldName, currentRecordIndex = 0) => {
    if (!hasCompareStepValidationError(fieldName, currentRecordIndex)) {
      return null;
    }

    // Convert display field name to snake_case validation field name
    const validationFieldName = fieldName.toUpperCase().replace(/\s+/g, '_') + '_VALIDATION';

    // Try to find the error with the full validation field name first
    let err = viewValidationErrors?.find(e => e.validation_field_name === validationFieldName);

    // If not found, try without the _VALIDATION suffix
    if (!err) {
      const fieldWithoutValidation = validationFieldName.replace(/_VALIDATION$/, '');
      err = viewValidationErrors?.find(e => e.validation_field_name === fieldWithoutValidation);
    }

    console.log('Error message lookup for field:', fieldName, 'validation field:', validationFieldName, 'found error:', err);

    return err ? err.validation_message : `Validation failed for ${formatHeaderName(fieldName)}`;
  };

  useEffect(() => {
    if (isCurrentStepCompare()) {
      // COMPARE step validation logic
      if (mainStepDataForCompare && mainStepDataForCompare.length > 0) {
        const fieldMap = {};

        mainStepDataForCompare.forEach((record, index) => {
          const validationFields = getValidationFields(record);

          validationFields.forEach((validationField) => {
            // For actual _VALIDATION fields from the data
            if (record[validationField] !== undefined) {
              const val = record[validationField];
              if (val === false || val === 'false' || val === 0 || val === '0') {
                // Convert validation field to display field name
                const fieldName = validationField.replace(/_VALIDATION$/, '');

                if (!fieldMap[validationField]) {
                  fieldMap[validationField] = {
                    fieldName: validationField,
                    displayFieldName: fieldName,
                    failedRows: []
                  };
                }

                fieldMap[validationField].failedRows.push({
                  id: `compare_record_${index}`,
                  investorName: record.INVESTOR || record.investor || record.PROVIDED_INVESTOR || record.PROVIDED_FUND_NAME || record.FUND_NAME || record.POSITION || `Record ${index + 1}`,
                  recordIndex: index
                });
              }
            } else {
              // For blank field validations (these are always considered errors)
              const fieldName = validationField.replace(/_VALIDATION$/, '');

              if (!fieldMap[validationField]) {
                fieldMap[validationField] = {
                  fieldName: validationField,
                  displayFieldName: fieldName,
                  failedRows: []
                };
              }

              fieldMap[validationField].failedRows.push({
                id: `compare_record_${index}`,
                investorName: record.INVESTOR || record.investor || record.PROVIDED_INVESTOR || record.PROVIDED_FUND_NAME || record.FUND_NAME || record.POSITION || `Record ${index + 1}`,
                recordIndex: index
              });
            }
          });
        });

        setValidationAlertsByField(Object.values(fieldMap));
      } else {
        setValidationAlertsByField([]);
      }
    } else {
      // Regular step validation logic
      if (comparisonData && comparisonData.length > 0) {
        const fieldMap = {};
        comparisonData.forEach((row) => {
          getValidationFields(row).forEach((key) => {
            // For actual _VALIDATION fields from the data
            if (row[key] !== undefined) {
              const val = row[key];
              if (val === false || val === 'false' || val === 0 || val === '0') {
                if (!fieldMap[key]) {
                  fieldMap[key] = { fieldName: key, failedRows: [] };
                }
                fieldMap[key].failedRows.push({
                  id: row.id,
                  investorName: row.INVESTOR || row.investor || row.PROVIDED_INVESTOR || row.PROVIDED_FUND_NAME || row.FUND_NAME || row.POSITION || row.id || 'Row',
                });
              }
            } else {
              // For blank field validations (these are always considered errors)
              if (!fieldMap[key]) {
                fieldMap[key] = { fieldName: key, failedRows: [] };
              }
              fieldMap[key].failedRows.push({
                id: row.id,
                investorName: row.INVESTOR || row.investor || row.PROVIDED_INVESTOR || row.PROVIDED_FUND_NAME || row.FUND_NAME || row.POSITION || row.id || 'Row',
              });
            }
          });
        });
        setValidationAlertsByField(Object.values(fieldMap));
      } else {
        setValidationAlertsByField([]);
      }
    }
  }, [comparisonData, mainStepDataForCompare, currentCompareRecordIndex]);

  const handleAlertClick = (fieldName, failedRows) => {
    if (isCurrentStepCompare()) {
      // COMPARE step alert click logic
      let highlightField = fieldName;
      if (fieldName.endsWith('_VALIDATION')) {
        highlightField = fieldName.replace(/_VALIDATION$/, '');
      }

      setSelectedAlertField(highlightField);

      // For COMPARE steps, navigate to the first failed record
      if (failedRows.length > 0 && failedRows[0].recordIndex !== undefined) {
        const recordIndex = failedRows[0].recordIndex;
        console.log('Navigating to record index:', recordIndex, 'for field:', highlightField);
        setCurrentCompareRecordIndex(recordIndex);

        // Also highlight the specific field in the compare grid
        setTimeout(() => {
          try {
            // Find the row in comparison data that matches this field
            const rowIndex = comparisonData.findIndex(r => r.field === highlightField);
            console.log('Found field row at index:', rowIndex, 'for field:', highlightField);
            console.log('Available fields in comparison data:', comparisonData.map(r => r.field));

            if (rowIndex !== -1 && apiRef.current) {
              // Scroll to the row and set focus
              apiRef.current.scrollToIndexes({ rowIndex, colIndex: 0 });
              // Try to set focus on the field cell
              const gridRow = comparisonData[rowIndex];
              if (gridRow && gridRow.id) {
                apiRef.current.setCellFocus(gridRow.id, 'field');
              }
            }
          } catch (e) {
            console.log('Error scrolling to field:', e);
          }
        }, 500); // Increased timeout to allow for grid update
      }
    } else {
      // Regular step alert click logic
      let highlightField = fieldName;
      if (fieldName.endsWith('_VALIDATION')) {
        highlightField = fieldName.replace(/_VALIDATION$/, '');
      }
      setSelectedAlertField(highlightField);
      const rowIds = failedRows.map(r => r.id);
      setHighlightedRowIds(rowIds);
      if (rowIds.length > 0 && apiRef.current) {
        setTimeout(() => {
          try {
            const rowIndex = comparisonData.findIndex(r => r.id === rowIds[0]);
            const colIndex = dynamicColumns.findIndex(c => c.field === highlightField);
            if (rowIndex !== -1 && colIndex !== -1) {
              apiRef.current.scrollToIndexes({ rowIndex, colIndex });
              apiRef.current.setCellFocus(rowIds[0], highlightField);
            }
          } catch (e) { }
        }, 200);
      }
    }
  };

  const getCellClassName = (params) => {
    if (!selectedAlertField) return '';

    if (isCurrentStepCompare()) {
      // For COMPARE steps, highlight the field row if it matches
      if (params.row && params.row.field === selectedAlertField) {
        return 'highlighted-alert-cell';
      }
    } else {
      // Regular step highlighting logic
      if (params.field === selectedAlertField && highlightedRowIds.includes(params.id)) {
        return 'highlighted-alert-cell';
      }
    }
    return '';
  };

  const totalFailedValidations = validationAlertsByField.reduce((sum, alert) => sum + alert.failedRows.length, 0);
  const isCurrentStepApproved = isStepApproved(steps?.[activeStep]);
  const blockingValidationStepIndex = getBlockingValidationStepIndexFromPayloads(steps, fundAdministrationValidations, approveErrorsMap);

  useEffect(() => {
    setTenantIntegrationLoading(true);
    const integrationId = localStorage.getItem('tenant_integration');
    if (integrationId) setTenantIntegrationId(integrationId);
    setTenantIntegrationLoading(false);
  }, []);


  const hasValidationFailures = () => {
    if (!comparisonData || comparisonData.length === 0) return false;
    return comparisonData.some(row =>
      Object.keys(row).some(key =>
        key.endsWith('NUMBER_OF_FAILURES') && Number(row[key]) > 0
      )
    );
  };

  const handleApprove = async () => {
    setApproveLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/approval`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!resp.ok) throw new Error(`Failed to approve: ${resp.status} ${resp.statusText}`);

      // Get the response data which contains the filename
      const responseData = await resp.json();
      const actualFileName = responseData.name || fileName;

      // Log the document approval with the actual filename from the response
      console.log('Attempting to log document approval...', { fileInstanceId, fileName: actualFileName });
      try {
        console.log('Calling AuditLoggers.documentApproved...');
        const result = await AuditLoggers.documentApproved(fileInstanceId, actualFileName);
        console.log('Audit log result:', result);
      } catch (error) {
        console.error('Error logging document approval:', error);
        // Don't fail the approval if logging fails
      }

      showSuccess('Document approved successfully', 'Approval');
      await recordStepApproval();
      setIsAlreadyApproved(true);
    } catch (err) {
      showError(`Approval failed: ${err.message}`, 'Approval Error');
      throw err;
    } finally {
      setApproveLoading(false);
      setApproveDialogOpen(false);
    }
  };

  const handleApproveButtonClick = () => {
    if (isAlreadyApproved) {
      setAlreadyApprovedDialogOpen(true);
      return;
    }
    if (hasValidationFailures()) {
      setValidationBlockDialogOpen(true);
    } else {
      setApproveDialogOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveExtractionTab(newValue);

    // Fetch data for the selected tab if not already loaded
    if (newValue === 1 && !baseInternalCasData && !baseInternalCasLoading) {
      fetchBaseInternalCasData();
    } else if (newValue === 2 && !landingInternalCasData && !landingInternalCasLoading) {
      fetchLandingInternalCasData();
    }
  };

  const handleInfoClick = async () => {
    if (!fastApiFileInstanceId || !workflowInstanceId) return;

    setExtractionLoading(true);
    setExtractionError(null);

    try {
      const token = localStorage.getItem('token');
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-extraction-json?file_instance_id=${fastApiFileInstanceId}&workflow_instance_id=${workflowInstanceId}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        apiUrl += `&tenant_id=${tenantId}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch extraction data: ${response.status}`);
      }

      const data = await response.json();
      setExtractionData(data.data?.[0] || null);
      setExtractionDialogOpen(true);
    } catch (err) {
      console.error('Error fetching extraction data:', err);
      setExtractionError(err.message);
    } finally {
      setExtractionLoading(false);
    }
  };

  const fetchBaseInternalCasData = async () => {
    if (!fastApiFileInstanceId || !workflowInstanceId) return;

    setBaseInternalCasLoading(true);
    setBaseInternalCasError(null);

    try {
      const token = localStorage.getItem('token');
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/base-internal-cas?file_id=${fastApiFileInstanceId}&workflow_id=${workflowInstanceId}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        apiUrl += `&tenant_id=${tenantId}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch base internal CAS data: ${response.status}`);
      }

      const data = await response.json();
      setBaseInternalCasData(data);
    } catch (err) {
      console.error('Error fetching base internal CAS data:', err);
      setBaseInternalCasError(err.message);
    } finally {
      setBaseInternalCasLoading(false);
    }
  };

  const fetchLandingInternalCasData = async () => {
    if (!fastApiFileInstanceId || !workflowInstanceId) return;

    setLandingInternalCasLoading(true);
    setLandingInternalCasError(null);

    try {
      const token = localStorage.getItem('token');
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/landing-internal-cas?file_id=${fastApiFileInstanceId}&workflow_id=${workflowInstanceId}`;

      // Add tenant_id parameter if available
      const tenantId = localStorage.getItem('tenant_integration');
      if (tenantId) {
        apiUrl += `&tenant_id=${tenantId}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch landing internal CAS data: ${response.status}`);
      }

      const data = await response.json();
      setLandingInternalCasData(data);
    } catch (err) {
      console.error('Error fetching landing internal CAS data:', err);
      setLandingInternalCasError(err.message);
    } finally {
      setLandingInternalCasLoading(false);
    }
  };

  const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;

    if (completed || active) {
      return (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#e8f5e8',
            border: '2px solid #4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4caf50'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: '1.2rem' }} titleAccess="Passed" />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: '#f5f5f5',
          border: '2px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#757575',
          fontSize: '0.875rem',
          fontWeight: 600
        }}
      >
        {icon}
      </Box>
    );
  };

  const documentCommentsFromInstance = useMemo(() => {
    const cc = fileInstanceDetails?.comments ?? fileInstanceDetails?.Comments;
    return Array.isArray(cc) ? cc : [];
  }, [fileInstanceDetails]);
  const documentCommentsCount = documentCommentsFromInstance.length;

  return (
    <>
      <Box
        sx={{
          ...harmonatePageSx,
          p: 0,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              Workflows
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBack}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <WorkIcon sx={{ fontSize: '1rem' }} />
              {selectedDocumentType}
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DescriptionIcon sx={{ fontSize: '1rem' }} />
              {fileName}
            </Typography>
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isHarmonateUser ? (
                <>
                  <IconButton
                    onClick={handleMaterializeTables}
                    sx={{ p: 0 }}
                    title="Materialize Table"
                  >
                    <RefreshIcon sx={{ color: ht.white, fontSize: '1.5rem' }} />
                  </IconButton>

                  {/* <IconButton
                    onClick={handleInfoClick}
                    disabled={extractionLoading}
                    sx={{ p: 0 }}
                    title="View extraction details"
                  >
                    {extractionLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <InfoIcon sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                    )}
                  </IconButton> */}
                </>
              ) : null}
              {fileInstanceId ? (
                <Tooltip title="Historical Activity">
                  <IconButton
                    onClick={() => setHistoryDrawerOpen(true)}
                    sx={harmonatePlainIconButtonSx}
                    aria-label="Open historical activity"
                    size="small"
                  >
                    <HistoryIcon sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              {fileInstanceId ? (
                <Tooltip title="Document comments">
                  <Badge
                    badgeContent={documentCommentsCount}
                    color="primary"
                    overlap="circular"
                    max={999}
                    invisible={documentCommentsCount === 0}
                  >
                    <IconButton
                      onClick={() => setCommentsDrawerOpen(true)}
                      sx={harmonatePlainIconButtonSx}
                      aria-label="Open document comments"
                      size="small"
                    >
                      <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: '1.5rem' }} />
                    </IconButton>
                  </Badge>
                </Tooltip>
              ) : null}
              <Badge
                badgeContent={isCurrentStepApproved || stepDataLoading ? 0 : (totalFailedValidations || 0)}
                color="error"
                overlap="circular"
                max={99999}
                sx={{ cursor: 'pointer' }}
                onClick={() => setAlertsOpen(true)}
              >
                <WarningIcon sx={{ color: '#f44336', fontSize: '1.5rem' }} />
              </Badge>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', position: 'relative', flexShrink: 0 }}>
          {showLeftArrow && (
            <IconButton
              size="small"
              sx={{
                bgcolor: '#f5f5f5',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                width: 36,
                borderRadius: '50%',
                p: 0,
                minWidth: 0,
                mr: 2,
                '&:hover': { bgcolor: '#e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }
              }}
              onClick={() => {
                if (stepperScrollRef.current) {
                  stepperScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
                }
              }}
              disabled={stepperScrollAtStart}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <Box
            ref={stepperScrollRef}
            sx={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              width: '100%',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              display: 'flex',
              alignItems: 'center',
              msOverflowStyle: 'none',
              py: 1
            }}
            onScroll={handleStepperScroll}
          >
            {stepperLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, width: '100%' }}>
                <CircularProgress size={20} sx={{ color: ht.green }} />
                <Typography variant="caption" sx={{ ml: 1, fontSize: '0.55rem' }}>
                  Loading workflow steps...
                </Typography>
              </Box>
            ) : stepperError ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, width: '100%' }}>
                <Typography variant="caption" color="error" sx={{ fontSize: '0.55rem' }}>
                  Error loading steps: {stepperError}
                </Typography>
              </Box>
            ) : (
              <Stepper
                activeStep={stepperUiActiveStep}
                alternativeLabel
                sx={{
                  display: 'inline-flex',
                  minWidth: 'max-content',
                  '& .MuiStep-root': { minWidth: '140px', maxWidth: '180px', mx: 1.5 },
                  '& .MuiStepLabel-root': {
                    cursor: 'pointer',
                    '&:hover': { '& .MuiStepLabel-label': { color: ht.white } }
                  },
                  '& .MuiStepLabel-label': {
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textAlign: 'center',
                    lineHeight: 1.3,
                    mt: 1,
                    color: ht.white,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    maxWidth: '100%',
                    minHeight: '2.5em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  },
                  '& .MuiStepLabel-label.Mui-active': { color: ht.white, fontWeight: 700 },
                  '& .MuiStepLabel-label.Mui-completed': { color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600 },
                  '& .MuiStepLabel-label.Mui-disabled': { color: 'rgba(255, 255, 255, 0.75)' },
                  '& .MuiStepLabel-label.Mui-error': { color: ht.red, fontWeight: 600 },
                  '& .MuiStepConnector-root': { top: 16, left: 'calc(-50% + 16px)', right: 'calc(50% + 16px)' },
                  '& .MuiStepConnector-line': { borderColor: 'rgba(255, 255, 255, 0.35)', borderTopWidth: 2 },
                  '& .MuiStepConnector-line.Mui-active': { borderColor: ht.blue },
                  '& .MuiStepConnector-line.Mui-completed': { borderColor: ht.green }
                }}
              >
                {runConditionVisibleStepIndices.map((realIndex) => {
                  const step = steps[realIndex];
                  const isBlockedByValidation = blockingValidationStepIndex !== -1 && realIndex > blockingValidationStepIndex;
                  const isDisabled = (isGridEditMode && realIndex !== activeStep) || isBlockedByValidation;
                  return (
                  <Step key={realIndex} disabled={isDisabled}>
                    <StepLabel
                      StepIconComponent={CustomStepIcon}
                      onClick={e => handleStepLabelClick(realIndex, e)}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1,
                      }}
                    >
                      {step.stepName}
                    </StepLabel>
                  </Step>
                )})}
              </Stepper>
            )}
          </Box>
          {showRightArrow && (
            <IconButton
              size="small"
              sx={{
                bgcolor: '#f5f5f5',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                width: 36,
                borderRadius: '50%',
                p: 0,
                minWidth: 0,
                ml: 2,
                '&:hover': { bgcolor: '#e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }
              }}
              onClick={() => {
                if (stepperScrollRef.current) {
                  stepperScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                }
              }}
              disabled={stepperScrollAtEnd}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', flex: 1, p: 2, gap: 2, minHeight: 0, alignItems: 'stretch', overflow: 'hidden' }}>
          <Box sx={{ flex: 2, maxWidth: '35%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: '100%', flex: 1, minHeight: 0, border: `1px solid ${ht.line}`, borderRadius: ht.cardRadius, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: ht.white }}>
              {pdfLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5' }}>
                  <CircularProgress sx={{ color: ht.green }} />
                  <Typography variant="body2" sx={{ ml: 2 }}>Loading PDF...</Typography>
                </Box>
              ) : pdfError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5', p: 3 }}>
                  <Alert severity="error" sx={{ mb: 2, maxWidth: '400px' }}>
                    <Typography variant="body2">
                      Failed to load PDF: {pdfError}
                    </Typography>
                  </Alert>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRetryPdf}
                    sx={{
                      color: ht.text,
                      borderColor: ht.darkBlue,
                      borderRadius: ht.buttonRadius,
                      textTransform: 'none',
                      bgcolor: ht.white,
                      '&:hover': {
                        borderColor: ht.darkBlue,
                        bgcolor: ht.secondaryTab,
                      },
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              ) : pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  title={`PDF Viewer - ${fileName}`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" color="text.secondary">
                    No PDF available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, maxWidth: '70%', minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1.5,
                gap: 2,
                justifyContent: 'space-between',
                flexShrink: 0,
                overflow: 'visible',
                pt: 1.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'visible' }}>
                {dropdownFieldName && rowsList.length > 0 && (
                  <Autocomplete
                    size="small"
                    options={rowsList}
                    value={selectedRow}
                    getOptionLabel={(option) => {
                      if (option === null || option === undefined) {
                        return '';
                      }
                      return String(option);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      // Compare values, handling both numbers and strings
                      return option === value || String(option) === String(value);
                    }}
                    onChange={(event, newValue) => {
                      setSelectedRow(newValue);
                      // Reset record index when changing selection
                      if (!isCurrentStepCompare()) {
                        setCurrentRecordIndex(0);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Rows"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          // Do not force shrink — empty/unfocused keeps label inside the field
                        }}
                        sx={{
                          minWidth: 450,
                          overflow: 'visible',
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&.Mui-focused': { color: `${ht.white} !important` },
                            '&.MuiInputLabel-shrink': {
                              color: `${ht.white} !important`,
                              bgcolor: 'transparent',
                              px: 0.5,
                            },
                            '&.Mui-focused.MuiInputLabel-shrink, &.MuiFormLabel-filled.MuiInputLabel-shrink': {
                              color: `${ht.white} !important`,
                            },
                          },
                          '& .MuiOutlinedInput-root': {
                            overflow: 'visible',
                            bgcolor: 'rgba(255, 255, 255, 0.08)',
                            color: ht.white,
                            borderRadius: ht.fieldRadius,
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.55)',
                              legend: { width: 'auto' },
                            },
                            '&:hover fieldset': { borderColor: ht.white },
                            '&.Mui-focused fieldset': { borderColor: ht.white },
                          },
                          '& .MuiInputBase-input': { color: ht.white },
                          '& .MuiSvgIcon-root': { color: ht.white },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option}
                      </Box>
                    )}
                    popupIcon={<SearchIcon />}
                    sx={{
                      minWidth: 450,
                      overflow: 'visible',
                      '& .MuiAutocomplete-popupIndicator': { transform: 'none', color: ht.white },
                      '& .MuiAutocomplete-clearIndicator': { color: ht.white },
                    }}
                    slotProps={{
                      popper: { sx: { width: "500px !important" } }
                    }}
                  />
                )}
                {isCurrentStepTransform() && filteredComparisonData.length > 1 && !viewGridEnabled && (
                  <IconButton
                    size="small"
                    onClick={goToPreviousRecord}
                    disabled={currentRecordIndex === 0}
                    sx={{
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#e0e0e0' },
                      '&:disabled': { opacity: 0.5 }
                    }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {isGridEditMode && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleGridEditSave}
                    sx={{ minWidth: 100 }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleGridEditCancel}
                    sx={{ minWidth: 100 }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
              {isCurrentStepTransform() && filteredComparisonData.length > 1 && !viewGridEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{
                    fontSize: '0.75rem',
                    color: ht.white,
                    minWidth: '80px',
                    textAlign: 'center'
                  }}>
                    {currentRecordIndex + 1} of {filteredComparisonData.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={goToNextRecord}
                    disabled={currentRecordIndex === filteredComparisonData.length - 1}
                    sx={{
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#e0e0e0' },
                      '&:disabled': { opacity: 0.5 }
                    }}
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {(isCurrentStepCompare() && mainStepDataForCompare.length > 1) && !viewGridEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <IconButton
                    size="small"
                    onClick={goToPreviousCompareRecord}
                    disabled={currentCompareRecordIndex === 0}
                    sx={{
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#e0e0e0' },
                      '&:disabled': { opacity: 0.5 }
                    }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{
                    fontSize: '0.75rem',
                    color: ht.white,
                    minWidth: '80px',
                    textAlign: 'center'
                  }}>
                    {currentCompareRecordIndex + 1} of {mainStepDataForCompare.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={goToNextCompareRecord}
                    disabled={currentCompareRecordIndex === mainStepDataForCompare.length - 1}
                    sx={{
                      bgcolor: '#f5f5f5',
                      '&:hover': { bgcolor: '#e0e0e0' },
                      '&:disabled': { opacity: 0.5 }
                    }}
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {comparisonError && (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.6rem' }}>
                  Error loading data
                </Typography>
              )}
            </Box>

            <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {(stepperLoading || stepDataLoading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <CircularProgress sx={{ color: ht.green }} />
                  <Typography variant="body2" sx={{ ml: 2, fontSize: '0.75rem' }}>
                    {stepDataLoading ? 'Loading step data...' : 'Loading workflow steps...'}
                  </Typography>
                </Box>
              ) : filteredComparisonData.length === 0 && !stepDataError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'transparent' }}>
                    No data available for this step
                  </Typography>
                </Box>
              ) : (
                ((steps[activeStep]?.stepName === 'Summary' && filteredComparisonData.length === 1) || isCurrentStepTransform()) && !viewGridEnabled ? (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', minHeight: 0, boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        {isCurrentStepTransform() ? `${steps[activeStep]?.stepName} Details` : 'Summary Details'}
                      </Typography>
                      {isSummaryApprovalStep() && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {printEnabled && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={handlePrint}
                              sx={{ minWidth: 80 }}
                            >
                              {printType === 'excel' ? 'Export' : 'Print'}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleApproveButtonClick}
                            disabled={approveLoading || isAlreadyApproved}
                            sx={{ minWidth: 100, opacity: isAlreadyApproved ? 0.6 : 1 }}
                          >
                            {approveLoading ? 'Approving...' : isAlreadyApproved ? 'Already Approved' : 'Approve'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: ht.cardRadius, border: `1px solid ${ht.line}`, bgcolor: ht.white, width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
                        <Stack spacing={1}>
                          {dynamicColumns.map((col) => {
                            const currentData = isCurrentStepTransform() ? getCurrentRecordData() : filteredComparisonData[0];
                            const value = currentData?.[col.field];
                            const hasError = !isCurrentStepApproved && isCurrentStepTransform() && validationAlertsByField.some(alert => {
                              let validationFieldName = alert.fieldName;
                              if (validationFieldName.endsWith('_VALIDATION')) {
                                validationFieldName = validationFieldName.replace(/_VALIDATION$/, '');
                              }
                              if (validationFieldName !== col.field) return false;
                              return alert.failedRows.some(failedRow => failedRow.id === currentData?.id);
                            });

                            return (
                              <Box key={col.field}
                                onClick={() => {
                                  // NFI-5884: click a value in the details list to see where it came from in the PDF (the same panel as the grid)
                                  if (isGridEditMode) return;
                                  const q = buildDetailsSourceQuery(col, currentData, dynamicColumns);
                                  if (q) setSourceQuery(q);
                                }}
                                sx={{
                                  cursor: isGridEditMode ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 0.5,
                                borderBottom: '1px solid #f0f0f0',
                                '&:last-child': { borderBottom: 'none' }
                              }}>
                                <Typography sx={{
                                  width: 200,
                                  maxWidth: 200,
                                  fontWeight: 600,
                                  color: '#000000',
                                  fontSize: '0.8rem',
                                  textAlign: 'left',
                                  flexShrink: 0,
                                  lineHeight: 2.3,
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal'
                                }}>
                                  {col.headerName || col.field}:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minHeight: '22px' }}>
                                  <Typography sx={{
                                    fontSize: '0.8rem',
                                    color: '#222',
                                    wordBreak: 'break-word',
                                    textAlign: 'left',
                                    flex: 1,
                                    fontWeight: 400,
                                    lineHeight: 1.2
                                  }}>
                                    {(() => {
                                      if (col.renderCell) {
                                        return col.renderCell({
                                          row: currentData,
                                          value,
                                          id: currentData?.id,
                                          field: col.field,
                                        });
                                      }
                                      return value == null ? 'Not Applicable' : String(value);
                                    })()}
                                  </Typography>
                                  {/* {hasError && (
                                    <WarningIcon
                                      sx={{ color: '#f44336', fontSize: '1rem', flexShrink: 0 }}
                                      titleAccess="Validation error"
                                    />
                                  )} */}
                                  {hasError && (
                                    <WarningIcon
                                      sx={{ color: '#f44336', fontSize: '1.5rem', flexShrink: 0 }}
                                      titleAccess={(() => {
                                        const alert = validationAlertsByField.find(alert => {
                                          let validationFieldName = alert.fieldName;
                                          if (validationFieldName.endsWith('_VALIDATION')) {
                                            validationFieldName = validationFieldName.replace(/_VALIDATION$/, '');
                                          }
                                          if (validationFieldName !== col.field) return false;
                                          return alert.failedRows.some(failedRow => failedRow.id === currentData?.id);
                                        });

                                        if (alert) {
                                          const alertField = alert.fieldName?.trim().toUpperCase();
                                          const err = viewValidationErrors?.find(e => e.validation_field_name?.trim().toUpperCase() === alertField);
                                          return err ? err.validation_message : `Validation failed for ${formatHeaderName(alert.fieldName.replace(/_VALIDATION$/, ''))}`;
                                        }

                                        return "Validation error";
                                      })()}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Paper>
                    {/* NFI-5884: the same source panel for the details list */}
                    <SourceHighlightPanel
                      open={!!sourceQuery}
                      onClose={() => setSourceQuery(null)}
                      pdfUrl={pdfUrl}
                      fileInstanceId={fileInstanceId}
                      fileName={fileName}
                      query={sourceQuery}
                    />
                  </Box>
                ) : (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between', flexShrink: 0 }}>
                      {isCurrentStepEditable() && (
                        <Tooltip title={!canEditWorkflowData ? 'You do not have permission to edit workflow data' : 'Edit'}>
                          <span>
                            <IconButton
                              onClick={isGridEditMode || !canEditWorkflowData ? undefined : handleEnterGridEditMode}
                              color={isGridEditMode ? 'primary' : 'default'}
                              disabled={!canEditWorkflowData}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        {printEnabled && (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handlePrint}
                            sx={{ minWidth: 80 }}
                          >
                            {printType === 'excel' ? 'Export' : 'Print'}
                          </Button>
                        )}
                        {isSummaryApprovalStep() && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleApproveButtonClick}
                            disabled={approveLoading || isAlreadyApproved}
                            sx={{ minWidth: 100, opacity: isAlreadyApproved ? 0.6 : 1 }}
                          >
                            {approveLoading ? 'Approving...' : isAlreadyApproved ? 'Already Approved' : 'Approve'}
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <DataGridPro
                      apiRef={apiRef}
                      rows={isGridEditMode ? Object.values(editedRows) : filteredComparisonData}
                      columns={getGridColumnsWithEdit()}
                      initialState={{
                        density: 'compact'
                      }}
                      disableRowSelectionOnClick
                      disableColumnSelector
                      disableDensitySelector
                      disableVirtualization={isGridEditMode}
                      sx={{
                        border: '1px solid #e0e0e0',
                        backgroundColor: ht.listBg,
                        flex: 1,
                        minHeight: 0,
                        height: '100%',
                        '& .MuiDataGrid-cell': { fontSize: '0.7rem', cursor: isGridEditMode ? 'default' : 'pointer' },
                        '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 600 },
                        '& .MuiDataGrid-row': {
                          cursor: 'default',
                          '&:hover': { backgroundColor: isGridEditMode ? undefined : '#f0f0f0' }
                        },
                        '& .highlighted-alert-cell': {
                          backgroundColor: '#ffeaea !important',
                          border: '2px solid #f44336',
                          transition: 'background 0.2s'
                        },
                        '& .MuiDataGrid-footerContainer': {
                          borderTop: '1px solid #e0e0e0',
                          backgroundColor: ht.listBg,
                          minHeight: '52px',
                          position: 'relative',
                          zIndex: 1
                        },
                        '& .MuiDataGrid-paginationPanel': { fontSize: '0.75rem' },
                        '& .MuiTablePagination-root': { fontSize: '0.75rem' },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }
                      }}
                      getRowId={(row) => row.id}
                      slots={{ toolbar: CustomToolbar }}
                      pagination={false}
                      paginationMode="client"
                      getCellClassName={getCellClassName}
                      onCellClick={(params) => {
                        // NFI-5884: click a value to see where it came from in the PDF; edit mode keeps today's behaviour
                        if (isGridEditMode) return;
                        const cols = getGridColumnsWithEdit();
                        const headerFor = (f) => { const c = cols.find((col) => col.field === f); return c ? c.headerName : undefined; };
                        const q = buildSourceQuery(params, headerFor);
                        if (q) setSourceQuery(q);
                      }}
                    />
                    <SourceHighlightPanel
                      open={!!sourceQuery}
                      onClose={() => setSourceQuery(null)}
                      pdfUrl={pdfUrl}
                      fileInstanceId={fileInstanceId}
                      fileName={fileName}
                      query={sourceQuery}
                    />
                  </Box>
                )
              )}
            </Paper>
          </Box>
        </Box>

        <Drawer
          anchor="right"
          open={alertsOpen}
          onClose={() => setAlertsOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 420,
              boxSizing: 'border-box',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: theme => (theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fdfdfd'),
              borderTopLeftRadius: 24,
              borderBottomLeftRadius: 24,
              boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.16)',
              borderLeft: theme => `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Validation overview
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Alerts
              </Typography>
            </Box>
            <IconButton onClick={() => setAlertsOpen(false)} size="small" sx={{ bgcolor: 'action.hover' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isCurrentStepApproved ? (
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ color: '#4caf50 !important' }} />}
                label="Approved"
                sx={{
                  bgcolor: 'rgba(76, 175, 80, 0.12)',
                  color: '#2e7d32',
                  fontWeight: 600,
                }}
              />
            ) : (
              <>
                <Chip
                  size="small"
                  icon={<WarningAmberIcon sx={{ color: 'error.main !important' }} />}
                  label={`${validationAlertsByField?.length || 0} field${validationAlertsByField?.length === 1 ? '' : 's'}`}
                  sx={{
                    bgcolor: 'rgba(244, 67, 54, 0.12)',
                    color: 'error.main',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'error.main' },
                  }}
                />
                <Chip
                  size="small"
                  label={`${totalFailedValidations} failure${totalFailedValidations === 1 ? '' : 's'}`}
                  variant="outlined"
                  sx={{ borderColor: 'rgba(99, 102, 241, 0.3)', color: 'text.primary', fontWeight: 500 }}
                />
                {approveErrorsEnabled && validationAlertsByField && validationAlertsByField.length > 0 && (
                  <Box sx={{ ml: 'auto' }}>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={handleApproveErrors}
                      sx={{
                        bgcolor: 'error.main',
                        '&:hover': {
                          bgcolor: 'error.dark',
                        }
                      }}
                    >
                      Approve Errors
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Stack>
          <Divider />
          {!isCurrentStepApproved && validationAlertsByField && validationAlertsByField.length > 0 ? (
            <Stack spacing={1.5} sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
              {validationAlertsByField.map((alert, index) => (
                <Accordion
                  key={index}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(148, 163, 184, 0.35)',
                    bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff'),
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 22 }} />}
                    sx={{
                      px: 2,
                      py: 1.5,
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                        gap: 1.5,
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: 'text.secondary',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(244, 67, 54, 0.12)',
                        color: 'error.main',
                      }}
                    >
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                        {isCurrentStepCompare() && alert.displayFieldName
                          ? formatHeaderName(alert.displayFieldName)
                          : formatHeaderName(alert.fieldName)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${alert.failedRows.length} row${alert.failedRows.length === 1 ? '' : 's'}`}
                      sx={{
                        bgcolor: 'rgba(244, 67, 54, 0.08)',
                        color: 'error.main',
                        fontWeight: 600,
                      }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'error.main', fontWeight: 500, mb: 1 }}>
                      {(() => {
                        const alertField = alert.fieldName?.trim().toUpperCase();
                        let err = viewValidationErrors?.find(e => e.validation_field_name?.trim().toUpperCase() === alertField);
                        if (!err && alertField.endsWith('_VALIDATION')) {
                          const fieldWithoutValidation = alertField.replace(/_VALIDATION$/, '');
                          err = viewValidationErrors?.find(e => e.validation_field_name?.trim().toUpperCase() === fieldWithoutValidation);
                        }
                        return err ? err.validation_message : `Validation failed for ${formatHeaderName(alert.fieldName.replace(/_VALIDATION$/, ''))}`;
                      })()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                      Failed rows
                    </Typography>
                    <Stack spacing={0.75}>
                      {alert.failedRows.map((row) => (
                        <Box
                          key={row.id}
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(148, 163, 184, 0.12)',
                          }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                            {row.investorName}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1,
              }}
            >
              <ErrorOutlineIcon color="disabled" sx={{ fontSize: 48 }} />
              <Typography variant="subtitle2" color="text.secondary">
                No alerts to review
              </Typography>
            </Box>
          )}
        </Drawer>

        <Toast
          open={toastState.open}
          onClose={hideToast}
          message={toastState.message}
          severity={toastState.severity}
          title={toastState.title}
          autoHideDuration={toastState.autoHideDuration}
          position={toastState.position}
          variant={toastState.variant}
        />

        {fileInstanceId ? (
          <HistoricalActivityDrawer
            open={historyDrawerOpen}
            onClose={() => setHistoryDrawerOpen(false)}
            fileInstanceId={fileInstanceId}
            workflowName={selectedDocumentType}
            fundName={resolveHistoricalFundName()}
            fileName={fileName}
            refreshKey={historyRefreshKey}
          />
        ) : null}

        {fileInstanceId ? (
          <DocumentCommentsDrawer
            open={commentsDrawerOpen}
            onClose={() => setCommentsDrawerOpen(false)}
            fileInstanceId={fileInstanceId}
            notifyParentSynced={fetchFileInstanceDetailsPayload}
            productScope={productScope}
          />
        ) : null}

        <Dialog
          open={approveDialogOpen}
          onClose={() => setApproveDialogOpen(false)}
          aria-labelledby="approve-dialog-title"
          aria-describedby="approve-dialog-description"
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx} id="approve-dialog-title">Confirm Approval</DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <DialogContentText id="approve-dialog-description">
              Are you sure you want to approve this document? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx} disabled={approveLoading}>
              Cancel
            </Button>
            <Button onClick={handleApprove} variant="contained" disabled={approveLoading} sx={harmonateProceedButtonSx}>
              {approveLoading ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={validationBlockDialogOpen}
          onClose={() => setValidationBlockDialogOpen(false)}
          aria-labelledby="validation-block-dialog-title"
          aria-describedby="validation-block-dialog-description"
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx} id="validation-block-dialog-title">Validation Errors Remaining</DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <DialogContentText id="validation-block-dialog-description">
              There are still validation errors to be resolved. Please fix all errors before approving.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setValidationBlockDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={alreadyApprovedDialogOpen}
          onClose={() => setAlreadyApprovedDialogOpen(false)}
          aria-labelledby="already-approved-dialog-title"
          aria-describedby="already-approved-dialog-description"
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx} id="already-approved-dialog-title">Already Approved</DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <DialogContentText id="already-approved-dialog-description">
              This document has already been approved. No further action is required.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAlreadyApprovedDialogOpen(false)} variant="contained" sx={harmonateProceedButtonSx} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Extraction Details Dialog */}
        <Dialog
          open={extractionDialogOpen}
          onClose={() => {
            setExtractionDialogOpen(false);
            setActiveExtractionTab(0);
            setBaseInternalCasData(null);
            setBaseInternalCasError(null);
            setLandingInternalCasData(null);
            setLandingInternalCasError(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>
              Extraction Details
            </Typography>
            <IconButton
              onClick={() => {
                setExtractionDialogOpen(false);
                setActiveExtractionTab(0);
                setBaseInternalCasData(null);
                setBaseInternalCasError(null);
                setLandingInternalCasData(null);
                setLandingInternalCasError(null);
              }}
              size="small"
              sx={harmonateDialogCloseIconSx}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ ...harmonateDialogContentSx, p: 3, backgroundColor: 'background.default' }}>
            {extractionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : extractionError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Error Loading Extraction Data</AlertTitle>
                {extractionError}
              </Alert>
            ) : extractionData ? (
              <Box sx={{ mt: 1 }}>
                <Tabs
                  value={activeExtractionTab}
                  onChange={handleTabChange}
                  sx={{
                    mb: 3,
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0'
                    }
                  }}
                >
                  <Tab
                    label="Extracted Data"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minWidth: 'auto',
                      mr: 2,
                      px: 1.5,
                      py: 1,
                      '&.Mui-selected': {
                        color: 'primary.main'
                      }
                    }}
                  />
                  <Tab
                    label="Base Internal CAS"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minWidth: 'auto',
                      mr: 2,
                      px: 1.5,
                      py: 1,
                      '&.Mui-selected': {
                        color: 'primary.main'
                      }
                    }}
                  />
                  <Tab
                    label="Landing Internal CAS"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minWidth: 'auto',
                      mr: 2,
                      px: 1.5,
                      py: 1,
                      '&.Mui-selected': {
                        color: 'primary.main'
                      }
                    }}
                  />
                </Tabs>

                <Box sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  p: 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  {activeExtractionTab === 0 && renderJsonData(extractionData)}

                  {activeExtractionTab === 1 && (
                    <>
                      {baseInternalCasLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : baseInternalCasError ? (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          <AlertTitle>Error Loading Base Internal CAS Data</AlertTitle>
                          {baseInternalCasError}
                        </Alert>
                      ) : baseInternalCasData ? (
                        renderJsonData(baseInternalCasData)
                      ) : (
                        <Typography color="text.secondary">
                          Click to load Base Internal CAS data
                        </Typography>
                      )}
                    </>
                  )}

                  {activeExtractionTab === 2 && (
                    <>
                      {landingInternalCasLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : landingInternalCasError ? (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          <AlertTitle>Error Loading Landing Internal CAS Data</AlertTitle>
                          {landingInternalCasError}
                        </Alert>
                      ) : landingInternalCasData ? (
                        renderJsonData(landingInternalCasData)
                      ) : (
                        <Typography color="text.secondary">
                          Click to load Landing Internal CAS data
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setExtractionDialogOpen(false);
                      setActiveExtractionTab(0);
                      setBaseInternalCasData(null);
                      setBaseInternalCasError(null);
                      setLandingInternalCasData(null);
                      setLandingInternalCasError(null);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 3,
                      py: 0.75,
                      borderRadius: 1
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                color: 'text.secondary'
              }}>
                <InfoIcon sx={{ color: '#4caf50', fontSize: 48, mb: 2, opacity: 0.7 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  No extraction data available
                </Typography>
                <Typography variant="body2">
                  The extraction details could not be loaded or no data is available for this document.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Materialization Dialog */}
        <Dialog
          open={materializationDialogOpen}
          maxWidth="sm"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>
            {materializationStatus?.includes('Saving') ? 'Saving Changes' :
              materializationStatus?.includes('Materializing') || materializationStatus?.includes('Re-Calculating') ? 'Re-Calculating Tables' :
                materializationStatus?.includes('completed') || materializationStatus?.includes('Failed') ? 'Processing Complete' :
                  'Re-Calculating Tables'}
          </DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {materializationLoading ? (
                <>
                  <CircularProgress sx={{ color: '#00D41F' }} />
                  <Typography variant="body1" color="text.secondary" align="center">
                    {materializationStatus || 'Processing in progress. Please wait...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    This may take a few moments
                  </Typography>
                </>
              ) : materializationStatus ? (
                <>
                  {materializationStatus.includes('Failed') ? (
                    <>
                      <ErrorOutlineIcon sx={{ color: '#d32f2f', fontSize: 64 }} />
                      <Typography variant="body1" color="error" align="center">
                        {materializationStatus}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon sx={{ color: '#00D41F', fontSize: 64 }} />
                      <Typography variant="body1" color="text.primary" align="center">
                        {materializationStatus}
                      </Typography>
                    </>
                  )}
                </>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setMaterializationDialogOpen(false);
                setMaterializationStatus(null);
              }}
              variant="contained"
              disabled={materializationLoading}
              sx={{
                bgcolor: '#00D41F',
                '&:hover': { bgcolor: '#00A018' }
              }}
            >
              {materializationLoading ? 'Please wait...' : 'OK'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>


    </>
  );
};

export default PDFViewer;